import React, { useContext, useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import imageCompression from 'browser-image-compression';
import { ThemeContext } from '../../hooks/ThemeContext';
import { UserContext } from '../../hooks/UserContext';
import { api } from '../../services/baseUrl';
import { 
  Users, 
  ShoppingBag, 
  Mail,
  Settings, 
  TrendingUp, 
  DollarSign,
  Package,
  MessageSquare,
  BarChart3,
  Plus,
  Download,
  Filter,
  Palette,
  Eye,
  Edit,
  Trash2,
  X,
  Power,
  FolderKanban
} from 'lucide-react';
import TableAdmin from '../../components/admin/TableAdmin';
import ModalAdmin from '../../components/admin/ModalAdmin';
import SearchBar from '../../components/admin/SearchBar';
import Pagination from '../../components/admin/Pagination';
import MessagingAdmin from '../../components/admin/MessagingAdmin';
import Watermark from '../../components/common/Watermark';

function Dashboard() {
    const { theme } = useContext(ThemeContext);
    const { user, isAuthenticated } = useContext(UserContext);
    const isDark = theme === 'dark';

    const [activeTab, setActiveTab] = useState('products');
    const [userSubTab, setUserSubTab] = useState('users');

    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [searchUser, setSearchUser] = useState('');
    const [currentPageUsers, setCurrentPageUsers] = useState(1);
    const [totalPagesUsers, setTotalPagesUsers] = useState(1);
    const [selectedUser, setSelectedUser] = useState(null);
    const [userModalOpen, setUserModalOpen] = useState(false);
    const [userFormMode, setUserFormMode] = useState('create');

    const [filteredOrders, setFilteredOrders] = useState([]);
    const [searchOrder, setSearchOrder] = useState('');
    const [currentPageOrders, setCurrentPageOrders] = useState(1);
    const [totalPagesOrders, setTotalPagesOrders] = useState(1);
    const [orderModalOpen, setOrderModalOpen] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);

    const [messages, setMessages] = useState([]);
    const [filteredMessages, setFilteredMessages] = useState([]);
    const [searchMessage, setSearchMessage] = useState('');
    const [messageTypeFilter, setMessageTypeFilter] = useState('all');
    const [currentPageMessages, setCurrentPageMessages] = useState(1);
    const [totalPagesMessages, setTotalPagesMessages] = useState(1);
    const [messageModalOpen, setMessageModalOpen] = useState(false);
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [replyText, setReplyText] = useState('');
    const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);

    const [projects, setProjects] = useState([]);
    const [filteredProjects, setFilteredProjects] = useState([]);
    const [searchProject, setSearchProject] = useState('');
    const [currentPageProjects, setCurrentPageProjects] = useState(1);
    const [totalPagesProjects, setTotalPagesProjects] = useState(1);
    const [projectModalOpen, setProjectModalOpen] = useState(false);
    const [selectedProject, setSelectedProject] = useState(null);
    const [projectFormMode, setProjectFormMode] = useState('create');

    const [products, setProducts] = useState([]);
    const [productsError, setProductsError] = useState(null);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [searchProduct, setSearchProduct] = useState('');
    const [productStatusTab, setProductStatusTab] = useState('all');
    const [productFilters, setProductFilters] = useState({
      saleType: '',
      status: '',
      creatorType: '',
      creatorId: '',
    });
    const [productSaleType, setProductSaleType] = useState('libre');
    const [allowedUsers, setAllowedUsers] = useState([]);
    const [currentPageProducts, setCurrentPageProducts] = useState(1);
    const [totalPagesProducts, setTotalPagesProducts] = useState(1);
    const [productModalOpen, setProductModalOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedAdminGalleryImage, setSelectedAdminGalleryImage] = useState(0);
    const [productFormMode, setProductFormMode] = useState('create');
    const [uploadedImages, setUploadedImages] = useState([]);
    const [imagePreviews, setImagePreviews] = useState([]);
    const [productFiles, setProductFiles] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [categoryModalOpen, setCategoryModalOpen] = useState(false);
    const [categoryFormMode, setCategoryFormMode] = useState('create');
    const [selectedCategoryData, setSelectedCategoryData] = useState(null);
    const fileInputRef = useRef(null);

    const [promoCodes, setPromoCodes] = useState([]);
    const [promoFlashId, setPromoFlashId] = useState(null);
    const [promoForm, setPromoForm] = useState({
      code: '',
      discount: '',
      start_date: '',
      end_date: '',
      id_product: ''
    });

    const [newsletterSubscribers, setNewsletterSubscribers] = useState([]);
    const [newsletterSearch, setNewsletterSearch] = useState('');
    const [siteSettings, setSiteSettings] = useState({ maintenance_mode: false, blocked_roles: [] });
    const [siteSettingsLoading, setSiteSettingsLoading] = useState(false);
    const [siteSettingsSaving, setSiteSettingsSaving] = useState(false);

    const [creators, setCreators] = useState([]);
    const [filteredCreators, setFilteredCreators] = useState([]);
    const [searchCreator, setSearchCreator] = useState('');
    const [currentPageCreators, setCurrentPageCreators] = useState(1);
    const [totalPagesCreators, setTotalPagesCreators] = useState(1);
    const [creatorModalOpen, setCreatorModalOpen] = useState(false);
    const [selectedCreator, setSelectedCreator] = useState(null);
    const [creatorFormMode, setCreatorFormMode] = useState('create');
    const [creatorsList, setCreatorsList] = useState([]);
    const [uploadedCreatorImage, setUploadedCreatorImage] = useState(null);

    const [earningsData, setEarningsData] = useState([]);
    const [searchEarnings, setSearchEarnings] = useState('');
    const [filteredEarnings, setFilteredEarnings] = useState([]);
    const [currentPageEarnings, setCurrentPageEarnings] = useState(1);
    const [totalPagesEarnings, setTotalPagesEarnings] = useState(1);

    const [stats, setStats] = useState({
      users: 0,
      orders: 0,
      revenue: 0,
      ursaff: 0,
      messages: 0,
    });

    const itemsPerPage = 10;
    const currentUserId = user?.id_user ?? user?.id;
    const currentUserRole = user?.id_role ?? user?.role_id ?? user?.role?.id_role ?? user?.role;
    const defaultCreatorType = currentUserRole === 3 ? 'krayne' : 'external';
    const defaultCreatorId = currentUserRole === 2 ? currentUserId : null;
    const defaultCreatorName = currentUserRole === 3
      ? 'Krayne Agency'
      : ((user?.nickname ?? `${user?.firstname ?? ''} ${user?.lastname ?? ''}`.trim()) || 'Créateur');
    const formatCurrency = (value) => `${Number(value || 0).toFixed(2)}€`;
    const normalizeCategoryName = (value) => String(value || '').trim().toLowerCase();
    const resolveSelectedCategoryValue = (productLike) => {
      if (!productLike) return '';

      const directValue = typeof productLike.category === 'object'
        ? (productLike.category?.name || productLike.category?.name_category || '')
        : (productLike.category || productLike.category_name || '');

      if (directValue) {
        const matchedByName = categories.find((category) => {
          const categoryName = category?.name || category?.name_category || '';
          return normalizeCategoryName(categoryName) === normalizeCategoryName(directValue);
        });

        if (matchedByName) {
          return matchedByName.name || matchedByName.name_category || '';
        }

        return directValue;
      }

      const categoryId = productLike.id_category
        || productLike.category_id
        || productLike.category?.id_category
        || productLike.category?.id;

      if (!categoryId) {
        return '';
      }

      const matchedCategory = categories.find((category) => String(category.id_category || category.id) === String(categoryId));
      return matchedCategory?.name || matchedCategory?.name_category || '';
    };

    const normalizeProductForForm = (productLike) => {
      if (!productLike) return null;

      return {
        ...productLike,
        id: productLike.id ?? productLike.id_product,
        id_product: productLike.id_product ?? productLike.id,
        name: productLike.name ?? productLike.name_product ?? '',
        description: productLike.description ?? productLike.description_product ?? '',
        price: productLike.price ?? productLike.price_ht ?? productLike.price_product ?? '',
        price_ht: productLike.price_ht ?? productLike.price ?? productLike.price_product ?? '',
        stock: productLike.stock ?? productLike.available_stock ?? productLike.quantity_product ?? 1,
        status: productLike.status ?? 'active',
        sale_type: productLike.sale_type ?? 'libre',
        creator_type: productLike.creator_type ?? defaultCreatorType,
        id_user_creator: productLike.id_user_creator ?? productLike.creator?.id_user ?? defaultCreatorId,
        creator_name: productLike.creator_name ?? productLike.creator?.nickname ?? defaultCreatorName,
        category: typeof productLike.category === 'object'
          ? (productLike.category?.name || productLike.category?.name_category || '')
          : (productLike.category || productLike.category_name || ''),
        allowed_users: Array.isArray(productLike.allowed_users) ? productLike.allowed_users : [],
        downloadable_files: Array.isArray(productLike.downloadable_files) ? productLike.downloadable_files : [],
        images: Array.isArray(productLike.images) ? productLike.images : [],
      };
    };

    const fetchUsers = async () => {
      try {
        const response = await api.get('/admin/users');
        const raw = response.data?.data || response.data?.users || response.data || [];
        const mapped = Array.isArray(raw) ? raw.map((u) => ({
          ...u,
          id: u.id_user ?? u.id,
          id_user: u.id_user ?? u.id,
          name: u.name || `${u.firstname ?? ''} ${u.lastname ?? ''}`.trim() || u.nickname || u.email,
        })) : [];
        setUsers(mapped);
        setFilteredUsers(mapped);
        setTotalPagesUsers(Math.max(1, Math.ceil(mapped.length / itemsPerPage)));
      } catch (error) {
        console.error('Erreur chargement utilisateurs:', error);
        setUsers([]);
        setFilteredUsers([]);
      }
    };

    const fetchCategories = async () => {
      try {
        const response = await api.get('/categories');
        const raw = response.data?.data || response.data || [];
        const mapped = Array.isArray(raw) ? raw : Object.values(raw || {});
        setCategories(mapped);
      } catch (error) {
        console.error('Erreur chargement catégories:', error);
        setCategories([]);
      }
    };

    const handleSaveCategory = async (categoryData) => {
      try {
        if (categoryFormMode === 'create') {
          await api.post('/categories', categoryData);
          toast.success('Catégorie créée avec succès');
        } else if (selectedCategoryData?.id_category || selectedCategoryData?.id) {
          await api.put(`/categories/${selectedCategoryData.id_category || selectedCategoryData.id}`, categoryData);
          toast.success('Catégorie modifiée avec succès');
        }

        setCategoryModalOpen(false);
        setSelectedCategoryData(null);
        fetchCategories();
      } catch (error) {
        console.error('Erreur sauvegarde catégorie:', error);
        toast.error(error.response?.data?.message || 'Erreur lors de la sauvegarde de la catégorie');
      }
    };

    const handleDeleteCategory = async (categoryId) => {
      if (!window.confirm('Supprimer cette catégorie ?')) return;
      try {
        await api.delete(`/categories/${categoryId}`);
        toast.success('Catégorie supprimée');
        fetchCategories();
      } catch (error) {
        console.error('Erreur suppression catégorie:', error);
        toast.error(error.response?.data?.message || 'Impossible de supprimer cette catégorie');
      }
    };

    const fetchEarnings = async (showSuccessToast = false) => {
      try {
        const response = await api.get('/admin/earnings');
        const earnings = response.data?.earnings || response.data?.data || [];
        setEarningsData(earnings);
        setFilteredEarnings(earnings);
        setTotalPagesEarnings(Math.max(1, Math.ceil(earnings.length / itemsPerPage)));
        if (showSuccessToast) {
          toast.success('Données rafraîchies');
        }
      } catch (error) {
        console.error('Erreur chargement gains:', error);
        setEarningsData([]);
        setFilteredEarnings([]);
        if (showSuccessToast) {
          toast.error('Impossible de charger les gains');
        }
      }
    };

    const fetchPromoCodes = async () => {
      try {
        const response = await api.get('/promo-codes');
        const codes = response.data?.data || response.data || [];
        setPromoCodes(Array.isArray(codes) ? codes : []);
      } catch (error) {
        console.error('Erreur chargement codes promo:', error);
        setPromoCodes([]);
      }
    };

    const fetchNewsletter = async () => {
      try {
        const response = await api.get('/admin/newsletter');
        const subscribers = response.data?.data || response.data || [];
        setNewsletterSubscribers(Array.isArray(subscribers) ? subscribers : []);
      } catch (error) {
        console.error('Erreur newsletter:', error);
        setNewsletterSubscribers([]);
      }
    };

    const fetchSiteSettings = async () => {
      try {
        setSiteSettingsLoading(true);
        const response = await api.get('/admin/site-settings');
        setSiteSettings(response.data?.data || { maintenance_mode: false, blocked_roles: [] });
      } catch (error) {
        console.error('Erreur paramètres site:', error);
        toast.error('Impossible de charger les paramètres du site');
        setSiteSettings({ maintenance_mode: false, blocked_roles: [] });
      } finally {
        setSiteSettingsLoading(false);
      }
    };

    const handleToggleBlockedRole = (roleId) => {
      setSiteSettings((prev) => {
        const blockedRoles = Array.isArray(prev.blocked_roles) ? prev.blocked_roles.map((role) => Number(role)) : [];
        const exists = blockedRoles.includes(roleId);

        return {
          ...prev,
          blocked_roles: exists
            ? blockedRoles.filter((role) => role !== roleId)
            : [...blockedRoles, roleId].sort((a, b) => a - b),
        };
      });
    };

    const handleSaveSiteSettings = async () => {
      try {
        setSiteSettingsSaving(true);
        const payload = {
          maintenance_mode: Boolean(siteSettings.maintenance_mode),
          blocked_roles: Array.isArray(siteSettings.blocked_roles)
            ? siteSettings.blocked_roles.map((role) => Number(role)).filter((role) => [1, 2].includes(role))
            : [],
        };

        const response = await api.put('/admin/site-settings', payload);
        const nextSettings = response.data?.data || payload;
        setSiteSettings(nextSettings);
        toast.success('Paramètres du site enregistrés');
        window.dispatchEvent(new Event('siteSettingsUpdated'));
      } catch (error) {
        console.error('Erreur sauvegarde paramètres site:', error);
        toast.error(error.response?.data?.message || 'Impossible d’enregistrer les paramètres');
      } finally {
        setSiteSettingsSaving(false);
      }
    };

    const handleTogglePromo = async (promo) => {
      try {
        await api.post(`/promo-codes/${promo.id_promo_code || promo.id}/activate`, {
          is_active: !promo.is_active,
        });
        setPromoFlashId(promo.id_promo_code || promo.id);
        setTimeout(() => setPromoFlashId(null), 2000);
        toast.success(promo.is_active ? 'Code désactivé' : 'Code activé');
        fetchPromoCodes();
      } catch (error) {
        console.error('Erreur promo:', error);
        toast.error('Impossible de mettre à jour le code promo');
      }
    };

    const handleCreatePromo = async () => {
      try {
        const payload = {
          code: String(promoForm.code || '').trim().toUpperCase(),
          discount: Number(promoForm.discount || 0),
          start_date: promoForm.start_date || null,
          end_date: promoForm.end_date || null,
          id_product: promoForm.id_product || null,
        };

        await api.post('/promo-codes', payload);
        toast.success('Code promo créé');
        setPromoForm({ code: '', discount: '', start_date: '', end_date: '', id_product: '' });
        fetchPromoCodes();
      } catch (error) {
        console.error('Erreur création promo:', error);
        toast.error(error.response?.data?.message || 'Erreur création code promo');
      }
    };

    const handleDeletePromo = async (promo) => {
      if (!window.confirm(`Supprimer ${promo?.code || 'ce code'} ?`)) return;
      try {
        await api.delete(`/promo-codes/${promo.id_promo_code || promo.id}`);
        setPromoCodes((prev) => prev.filter((item) => (item.id_promo_code || item.id) !== (promo.id_promo_code || promo.id)));
        toast.success('Code promo supprimé');
      } catch (error) {
        console.error('Erreur suppression promo:', error);
        toast.error('Impossible de supprimer le code promo');
      }
    };

    const handleToggleNewsletter = async (subscriber) => {
      try {
        await api.patch(`/admin/newsletter/${subscriber.id}/toggle`);
        fetchNewsletter();
      } catch (error) {
        console.error('Erreur newsletter toggle:', error);
        toast.error('Impossible de mettre à jour cet abonnement');
      }
    };

    const handleDeleteNewsletter = async (subscriber) => {
      try {
        await api.delete(`/admin/newsletter/${subscriber.id}`);
        fetchNewsletter();
      } catch (error) {
        console.error('Erreur suppression newsletter:', error);
        toast.error('Suppression impossible');
      }
    };

    useEffect(() => {
      fetchUsers();
      fetchCategories();
    }, []);

    useEffect(() => {
      if (activeTab === 'promos') {
        fetchPromoCodes();
      }

      if (activeTab === 'newsletter') {
        fetchNewsletter();
      }

      if (activeTab === 'settings') {
        fetchSiteSettings();
      }
    }, [activeTab]);

    useEffect(() => {
      if (!productModalOpen || !selectedProduct || categories.length === 0) {
        return;
      }

      const resolvedCategory = resolveSelectedCategoryValue(selectedProduct);
      if (resolvedCategory && resolvedCategory !== selectedCategory) {
        setSelectedCategory(resolvedCategory);
      }
    }, [categories, productModalOpen, selectedProduct]);

    useEffect(() => {
      setSelectedAdminGalleryImage(0);
    }, [selectedProduct, productFormMode, productModalOpen]);

// ...existing code...
// ...existing code...
    // Filtrer les utilisateurs selon le sub-tab actif
    useEffect(() => {
      // Filtrer par rôle selon le sub-tab
      const usersByRole = userSubTab === 'users' 
        ? users.filter(u => u.id_role === 1) // Seulement les utilisateurs (role 1)
        : users; // Tous les users pour le sub-tab créateurs (filtrage fait ailleurs)
      
      const query = searchUser.toLowerCase();
      const filtered = query
        ? usersByRole.filter(u =>
            (u.name || '').toLowerCase().includes(query) ||
            (u.email || '').toLowerCase().includes(query) ||
            (u.nickname || '').toLowerCase().includes(query)
          )
        : usersByRole;
      setFilteredUsers(filtered);
      setCurrentPageUsers(1);
      setTotalPagesUsers(Math.max(1, Math.ceil(filtered.length / itemsPerPage)));
    }, [searchUser, users, userSubTab, itemsPerPage]);
    
    // Filtrer les gains
    useEffect(() => {
      const query = searchEarnings.toLowerCase();
      const filtered = query
        ? earningsData.filter(e =>
            (e.creator_name || '').toLowerCase().includes(query) ||
            (e.creator_email || '').toLowerCase().includes(query)
          )
        : earningsData;
      setFilteredEarnings(filtered);
      setCurrentPageEarnings(1);
      setTotalPagesEarnings(Math.max(1, Math.ceil(filtered.length / itemsPerPage)));
    }, [searchEarnings, earningsData]);

    useEffect(() => {
      const query = searchMessage.toLowerCase();
      const filtered = query
        ? messages.filter(m =>
            (m.name || '').toLowerCase().includes(query) ||
            (m.email || '').toLowerCase().includes(query) ||
            (m.subject || '').toLowerCase().includes(query) ||
            (m.message || '').toLowerCase().includes(query)
          )
        : messages;

      setFilteredMessages(filtered);
      setTotalPagesMessages(Math.max(1, Math.ceil(filtered.length / itemsPerPage)));
      setCurrentPageMessages(1);
    }, [searchMessage, messages]);

    useEffect(() => {
      const query = searchProject.toLowerCase();
      const filtered = query
        ? projects.filter(p =>
            (p.name || '').toLowerCase().includes(query) ||
            (p.user_name || '').toLowerCase().includes(query) ||
            (p.status || '').toLowerCase().includes(query)
          )
        : projects;

      setFilteredProjects(filtered);
      setTotalPagesProjects(Math.max(1, Math.ceil(filtered.length / itemsPerPage)));
      setCurrentPageProjects(1);
    }, [searchProject, projects]);
    
    // Charger les commandes
    useEffect(() => {
      if (activeTab === 'orders') {
        fetchOrders();
      }
    }, [activeTab]);
    
    const fetchOrders = async () => {
      try {
        const response = await api.get('/admin/orders');
        const raw = response.data?.data || [];
        const mapped = raw.map((order) => ({
          id: order.id,
          order_id: order.reference || `#${order.id}`,
          client: order.user_name || 'Client',
          service: order.product_name || 'Produit',
          amount: order.total ?? order.amount ?? 0,
          status: order.status_label || order.status || 'En attente',
          date: order.created_at,
        }));
        setFilteredOrders(mapped);
        setTotalPagesOrders(Math.max(1, Math.ceil(mapped.length / itemsPerPage)));
      } catch (error) {
        console.error('Erreur lors du chargement des commandes:', error);
        setFilteredOrders([]);
        setTotalPagesOrders(1);
      }
    };
    
    // Charger les messages de contact
    useEffect(() => {
      if (activeTab === 'messages') {
        fetchMessages();
      }
    }, [activeTab, messageTypeFilter]);
    
    const fetchMessages = async () => {
      try {
        // Utiliser le nouveau endpoint avec query param
        const endpoint = `/contact/all-messages?message_type=${messageTypeFilter}`;
        
        const response = await api.get(endpoint);
        
        let mapped = [];
        const data = response.data?.data || {};
        
        // Combiner messages de contact et conversations selon le filtre
        if (messageTypeFilter === 'all') {
          // TOUS : Combiner contact_messages + conversations
          const contactMessages = (data.contact_messages || []).map((m) => ({
            id: `contact_${m.id}`,
            type: 'contact',
            name: m.name,
            email: m.email,
            subject: m.service || 'Message de contact',
            message: m.message,
            status: m.status || 'nouveau',
            response: m.response || null,
            response_date: m.response_date || null,
            created_at: m.created_at,
            request_type: m.request_type || 'support',
          }));
          
          const conversations = (data.conversations || []).map((conv) => ({
            id: `conv_${conv.conversation_id}`,
            type: 'conversation',
            name: conv.sender?.name || 'Utilisateur',
            email: conv.sender?.email || 'N/A',
            subject: `Conversation: ${conv.sender?.name} → ${conv.receiver?.name}`,
            message: conv.last_message,
            status: conv.is_closed ? 'clôturé' : 'actif',
            created_at: conv.last_message_date,
            message_count: conv.message_count,
            conversation_id: conv.conversation_id,
          }));
          
          mapped = [...contactMessages, ...conversations];
          
        } else if (messageTypeFilter === 'contact') {
          // FORMULAIRE DE CONTACT : Seulement contact_messages
          mapped = (data.contact_messages || []).map((m) => ({
            id: `contact_${m.id}`,
            type: 'contact',
            name: m.name,
            email: m.email,
            subject: m.service || 'Message de contact',
            message: m.message,
            status: m.status || 'nouveau',
            response: m.response || null,
            response_date: m.response_date || null,
            created_at: m.created_at,
            request_type: m.request_type || 'support',
          }));
          
        } else if (messageTypeFilter === 'conversations') {
          // CONVERSATIONS : Seulement utilisateur → créateur
          mapped = (data.conversations || []).map((conv) => ({
            id: `conv_${conv.conversation_id}`,
            type: 'conversation',
            name: conv.sender?.name || 'Utilisateur',
            email: conv.sender?.email || 'N/A',
            subject: `${conv.sender?.name} → ${conv.receiver?.name}`,
            message: conv.last_message,
            status: conv.is_closed ? 'clôturé' : 'actif',
            created_at: conv.last_message_date,
            message_count: conv.message_count,
            conversation_id: conv.conversation_id,
          }));
        }
        
        // Trier par date décroissante
        mapped.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        
        setMessages(mapped);
        setFilteredMessages(mapped);
        setTotalPagesMessages(Math.max(1, Math.ceil(mapped.length / itemsPerPage)));
        
        // Compter les messages non lus
        const unreadCount = mapped.filter(m => m.status === 'nouveau').length;
        setUnreadMessagesCount(unreadCount);
        
      } catch (error) {
        console.error('Erreur lors du chargement des messages:', error);
      }
    };

    // Charger les projets
    useEffect(() => {
      if (activeTab === 'projects') {
        fetchProjects();
      }
    }, [activeTab]);

    const fetchProjects = async () => {
      try {
        const response = await api.get('/admin/projects');
        const raw = response.data?.projects || [];
        const mapped = raw.map((p) => ({
          id_project: p.id_project ?? p.id,
          name: p.name,
          type: p.type,
          status: p.status,
          progress: p.progress ?? 0,
          description: p.description,
          start_date: p.start_date,
          end_date: p.end_date,
          updated_at: p.updated_at,
          id_user: p.id_user,
          user_name: p.user ? (p.user.nickname || `${p.user.firstname ?? ''} ${p.user.lastname ?? ''}`.trim() || p.user.email) : p.user_name
        }));
        setProjects(mapped);
        setFilteredProjects(mapped);
        setTotalPagesProjects(Math.max(1, Math.ceil(mapped.length / itemsPerPage)));
      } catch (error) {
        console.error('Erreur lors du chargement des projets:', error);
        setProjects([]);
        setFilteredProjects([]);
      }
    };
    
    // Charger les produits et créateurs
    useEffect(() => {
      if (activeTab === 'products') {
        fetchProducts();
      } else if (activeTab === 'creators') {
        fetchCreators();
      }
    }, [activeTab]);
    
    // Charger la liste des créateurs pour le dropdown
    useEffect(() => {
      fetchCreatorsList();
    }, []);
    
    const fetchCreatorsList = async () => {
      try {
        const response = await api.get('/admin/creators/list');
        const raw = response.data?.data || response.data || [];
        const mapped = raw.map((c) => ({
          id: c.id_user ?? c.id,
          id_user: c.id_user ?? c.id,
          nickname: c.nickname ?? '',
          fullname: `${c.firstname ?? ''} ${c.lastname ?? ''}`.trim() || c.nickname || c.email,
          email: c.email ?? '',
        }));
        setCreatorsList(mapped);
      } catch (error) {
        console.error('Erreur lors du chargement des créateurs:', error);
        setCreatorsList([]);
      }
    };
    
    const resolveProductImageUrl = (productOrImage) => {
      if (!productOrImage) return null;

      const firstImage = Array.isArray(productOrImage.images) && productOrImage.images.length > 0
        ? productOrImage.images[0]
        : null;

      const rawPath = productOrImage.path_watermarked
        || productOrImage.path_image
        || productOrImage.image
        || productOrImage.image_url
        || productOrImage.picture_product
        || (typeof firstImage === 'string'
          ? firstImage
          : (firstImage?.path_watermarked || firstImage?.path_image || null));

      if (!rawPath) return null;

      const apiBaseUrl = api?.defaults?.baseURL || 'https://api.krayneagency.fr/api';
      const apiOrigin = apiBaseUrl.replace(/\/api\/?$/, '');

      if (String(rawPath).startsWith('http')) {
        if (String(rawPath).includes('/api/images/')) {
          try {
            const parsedUrl = new URL(String(rawPath));
            return `${apiOrigin}${parsedUrl.pathname}`;
          } catch {
            return String(rawPath);
          }
        }

        return String(rawPath);
      }

      let cleanPath = String(rawPath).replace(/^\/+/, '');
      if (cleanPath.startsWith('storage/')) cleanPath = cleanPath.substring(8);

      return `${apiOrigin}/api/images/${cleanPath}`;
    };

    const fetchProducts = async () => {
      try {
        // Admin voit tous les produits
        const response = await api.get(`/admin/products`);
        const raw = response.data?.data || response.data || [];
        const mapped = raw.map((p) => {
          const firstImg = Array.isArray(p.images) && p.images.length > 0 ? p.images[0] : null;
          const firstImgPath = typeof firstImg === 'string'
            ? firstImg
            : (firstImg?.path_watermarked || firstImg?.path_image || null);
          const normalizedImages = Array.isArray(p.images)
            ? p.images.map((img) => {
                if (typeof img === 'string') {
                  const imageUrl = resolveProductImageUrl({ image: img });
                  return {
                    path_image: imageUrl,
                    path_watermarked: imageUrl,
                  };
                }

                return {
                  ...img,
                  path_image: resolveProductImageUrl({ path_image: img?.path_image }),
                  path_watermarked: resolveProductImageUrl({ path_watermarked: img?.path_watermarked || img?.path_image }),
                };
              })
            : [];
          // Extraire le nom de catégorie (peut être un objet ou une string)
          const catName = typeof p.category === 'object'
            ? (p.category?.name || p.category?.name_category || '')
            : (p.category || p.category_name || '');
          
          // Debug: afficher les données reçues
          console.log('Produit reçu:', {
            id: p.id_product,
            name: p.name_product,
            stock: p.stock,
            inventory_stock: p.inventory?.stock,
            available_stock: p.available_stock,
            status: p.status
          });
          
          return ({
          id: p.id_product ?? p.id,
          id_product: p.id_product ?? p.id,
          name: p.name ?? p.name_product ?? '',
          category: catName,
          price: p.price_ttc ?? p.price ?? p.price_product ?? 0,
          price_ht: p.price ?? p.price_product ?? 0,
          stock: p.inventory?.stock ?? p.stock ?? 0,
          sale_type: p.sale_type ?? '',
          status: p.status ?? '',
          creator_type: p.creator_type ?? '',
          creator_name: p.creator_name ?? '',
          id_user_creator: p.id_user_creator ?? null,
          description: p.description ?? p.description_product ?? '',
          images: normalizedImages,
          image: resolveProductImageUrl({ image: p.image ?? firstImgPath ?? null, images: normalizedImages }),
          available_stock: p.inventory?.available_stock ?? p.available_stock ?? p.stock ?? 0,
          price_ttc: p.price_ttc ?? p.price ?? p.price_product ?? 0,
          allowed_users: p.allowed_users ?? [],
          created_at: p.created_at ?? p.createdAt ?? '',
          });
        });
        setProducts(mapped);
        setFilteredProducts(mapped);
        setTotalPagesProducts(Math.max(1, Math.ceil(mapped.length / itemsPerPage)));
        setProductsError(null);
      } catch (error) {
        console.error('Erreur lors du chargement des produits:', error);
        setProducts([]);
        setFilteredProducts([]);
        const serverMessage = error.response?.data?.message || error.message || 'Erreur inconnue';
        setProductsError(serverMessage);
      }
    };
    
    // Filtrer les produits (recherche, filtres avancés, visibilité unique)
    useEffect(() => {
      const query = searchProduct.trim().toLowerCase();

      const nextFiltered = products.filter(p => {
        const matchesQuery = query
          ? (p.name || '').toLowerCase().includes(query)
            || (p.category || '').toLowerCase().includes(query)
            || (p.creator_name || '').toLowerCase().includes(query)
          : true;

        const matchesSaleType = productFilters.saleType
          ? (p.sale_type || '').toLowerCase() === productFilters.saleType
          : true;

        // Filtrage par onglet de statut (prioritaire sur le filtre dropdown)
        const matchesStatusTab = productStatusTab === 'all'
          ? true
          : (p.status || '').toLowerCase() === productStatusTab;

        const matchesStatus = productFilters.status
          ? (p.status || '').toLowerCase() === productFilters.status
          : true;

        const matchesCreatorType = productFilters.creatorType
          ? (p.creator_type || '').toLowerCase() === productFilters.creatorType
          : true;

        const matchesCreatorId = productFilters.creatorId
          ? String(p.id_user_creator ?? '') === String(productFilters.creatorId)
          : true;

        // Si produit unique et restreint : indiquer via badge, mais filtrer seulement si créateur sélectionné
        return matchesQuery && matchesSaleType && matchesStatusTab && matchesStatus && matchesCreatorType && matchesCreatorId;
      });

      setFilteredProducts(nextFiltered);
      setTotalPagesProducts(Math.max(1, Math.ceil(nextFiltered.length / itemsPerPage)));
      setCurrentPageProducts(1);
    }, [searchProduct, products, itemsPerPage, productFilters, productStatusTab]);
    
    // Filtrer les créateurs depuis users (role 2)
    useEffect(() => {
      const creatorsFromUsers = users.filter(u => u.id_role === 2);
      
      if (searchCreator.trim() === '') {
        setFilteredCreators(creatorsFromUsers);
      } else {
        const filtered = creatorsFromUsers.filter(c => 
          c.nickname?.toLowerCase().includes(searchCreator.toLowerCase()) ||
          c.firstname?.toLowerCase().includes(searchCreator.toLowerCase()) ||
          c.lastname?.toLowerCase().includes(searchCreator.toLowerCase()) ||
          c.email?.toLowerCase().includes(searchCreator.toLowerCase())
        );
        setFilteredCreators(filtered);
      }
      setTotalPagesCreators(Math.max(1, Math.ceil(filteredCreators.length / itemsPerPage)));
      setCurrentPageCreators(1);
    }, [searchCreator, users, itemsPerPage]);
    
    // Gestionnaires d'événements
    const handleCreateUser = () => {
      setUserFormMode('create');
      setSelectedUser(null);
      setUserModalOpen(true);
    };
    
    const handleEditUser = (user) => {
      setUserFormMode('edit');
      setSelectedUser(user);
      setUserModalOpen(true);
    };
    
    const handleViewUser = (user) => {
      setUserFormMode('view');
      setSelectedUser(user);
      setUserModalOpen(true);
    };
    
    const handleDeleteUser = async (user) => {
      if (window.confirm(`Voulez-vous vraiment supprimer l'utilisateur ${user.nickname} ?`)) {
        try {
          await api.delete(`/admin/users/${user.id_user || user.id}`);
          toast.success('Utilisateur supprimé avec succès');
          fetchUsers();
        } catch (error) {
          console.error('Erreur lors de la suppression:', error);
          toast.error(error.response?.data?.message || 'Erreur lors de la suppression');
        }
      }
    };
    
    const handleSaveUser = async (userData) => {
      try {
        if (userFormMode === 'create') {
          await api.post('/admin/users', userData);
          toast.success('Utilisateur créé avec succès');
        } else {
          const response = await api.put(`/admin/users/${selectedUser.id_user || selectedUser.id}`, userData);
          toast.success('Utilisateur modifié avec succès');
          
          // Si l'email a changé, afficher un message de notification
          if (response.data?.emailChanged) {
            toast.info('L\'utilisateur devra vérifier son nouvel email pour réactiver son compte', { duration: 5000 });
          }
          
          // Si le statut a changé, afficher un message spécifique
          if (response.data?.statusChanged) {
            if (userData.status === 'inactive') {
              toast.error('Compte utilisateur désactivé - il ne pourra plus se connecter');
            } else {
              toast.success('Compte utilisateur réactivé');
            }
          }
        }
        setUserModalOpen(false);
        fetchUsers();
      } catch (error) {
        console.error('Erreur lors de la sauvegarde:', error);
        toast.error(error.response?.data?.message || 'Erreur lors de la sauvegarde');
      }
    };
    
    const handleViewOrder = (order) => {
      setSelectedOrder(order);
      setOrderModalOpen(true);
    };
    
    const handleViewMessage = async (message) => {
      setSelectedMessage(message);
      setReplyText(message?.response || '');
      setMessageModalOpen(true);
      
      // Marquer comme lu si c'est un nouveau message
      if (message.status === 'nouveau') {
        try {
          await api.put(`/contact/messages/${message.id}/mark-read`);
          // Mettre à jour le message localement
          const updatedMessages = messages.map(m => 
            m.id === message.id ? { ...m, status: 'lu' } : m
          );
          setMessages(updatedMessages);
          setFilteredMessages(updatedMessages.filter(m => {
            const query = searchMessage.toLowerCase();
            if (!query) return true;
            return (m.name || '').toLowerCase().includes(query) ||
                   (m.email || '').toLowerCase().includes(query) ||
                   (m.subject || '').toLowerCase().includes(query) ||
                   (m.message || '').toLowerCase().includes(query);
          }));
          // Décrémenter le compteur de non lus
          setUnreadMessagesCount(prev => Math.max(0, prev - 1));
        } catch (error) {
          console.error('Erreur marquage message lu:', error);
        }
      }
    };

    const handleReplyMessage = async () => {
      if (!selectedMessage?.id || !replyText.trim()) return;
      try {
        await api.post(`/contact/messages/${selectedMessage.id}/reply`, { response: replyText.trim() });
        toast.success('Réponse envoyée');
        setMessageModalOpen(false);
        fetchMessages();
      } catch (error) {
        console.error('Erreur réponse message:', error);
        toast.error(error.response?.data?.message || 'Erreur lors de la réponse');
      }
    };
    
    const handleDeleteMessage = async (message) => {
      if (window.confirm('Voulez-vous vraiment supprimer ce message ?')) {
        try {
          // await api.delete(`/admin/messages/${message.id}`);
          setMessages(messages.filter(m => m.id !== message.id));
          toast.success('Message supprimé');
        } catch (error) {
          console.error('Erreur:', error);
        }
      }
    };
    
    // Gestionnaires projets
    const handleCreateProject = () => {
      setProjectFormMode('create');
      setSelectedProject({
        id_user: '',
        name: '',
        type: '',
        status: 'En cours',
        progress: 0,
        description: '',
        start_date: '',
        end_date: ''
      });
      setProjectModalOpen(true);
    };

    const handleEditProject = (project) => {
      setProjectFormMode('edit');
      setSelectedProject(project);
      setProjectModalOpen(true);
    };

    const handleViewProject = (project) => {
      setProjectFormMode('view');
      setSelectedProject(project);
      setProjectModalOpen(true);
    };

    const handleDeleteProject = async (project) => {
      if (window.confirm(`Voulez-vous vraiment supprimer le projet "${project.name}" ?`)) {
        try {
          await api.delete(`/admin/projects/${project.id_project || project.id}`);
          toast.success('Projet supprimé');
          fetchProjects();
        } catch (error) {
          console.error('Erreur suppression projet:', error);
          toast.error(error.response?.data?.message || 'Erreur lors de la suppression');
        }
      }
    };

    const handleSaveProject = async (projectData) => {
      try {
        if (projectFormMode === 'create') {
          await api.post('/admin/projects', projectData);
          toast.success('Projet créé');
        } else {
          await api.put(`/admin/projects/${selectedProject.id_project || selectedProject.id}`, projectData);
          toast.success('Projet modifié');
        }
        setProjectModalOpen(false);
        fetchProjects();
      } catch (error) {
        console.error('Erreur sauvegarde projet:', error);
        toast.error(error.response?.data?.message || 'Erreur lors de la sauvegarde');
      }
    };
    
    // Gestionnaires produits
    const handleCreateProduct = () => {
      setProductFormMode('create');
      setSelectedProduct(normalizeProductForForm({
        name: '',
        category: '',
        price: '',
        stock: 1,
        sale_type: 'libre',
        status: 'active',
        creator_type: defaultCreatorType,
        id_user_creator: defaultCreatorId,
        creator_name: defaultCreatorName,
        description: '',
      }));
      setProductSaleType('libre');
      setSelectedCategory('');
      setAllowedUsers([]);
      setUploadedImages([]);
      setImagePreviews([]);
      setProductFiles([]);
      setProductModalOpen(true);
    };
    
    const handleEditProduct = async (product) => {
      setProductFormMode('edit');
      setUploadedImages([]);
      setImagePreviews([]);
      setProductFiles([]);

      // Charger le produit complet (avec images) pour l'aperçu fiable
      let full = product;
      try {
        const id = product.id_product || product.id;
        const { data } = await api.get(`/admin/products/${id}`);
        full = data?.data || data || product;
      } catch (e) {
        console.warn('Impossible de charger le détail du produit, utilisation des données de la ligne:', e?.message);
      }

      const normalizedProduct = normalizeProductForForm(full);

      setSelectedProduct(normalizedProduct);
      setProductSaleType(normalizedProduct?.sale_type || 'libre');
      const catName = resolveSelectedCategoryValue(normalizedProduct);
      setSelectedCategory(catName);
      // Extraire les IDs des utilisateurs autorisés (peut être array d'objets ou array de strings)
      const userIds = (normalizedProduct?.allowed_users || []).map(u => 
        typeof u === 'object' ? String(u.id_user || u.id) : String(u)
      );
      setAllowedUsers(userIds);
      setProductModalOpen(true);

      // Initialiser le calcul TVA après ouverture du modal
      setTimeout(() => {
        const priceInput = document.querySelector('input[name="price"]');
        const priceHT = (() => {
          if (!priceInput) return parseFloat(normalizedProduct?.price_ht) || parseFloat(normalizedProduct?.price) || 0;
          return parseFloat(priceInput.value) || parseFloat(normalizedProduct?.price_ht) || parseFloat(normalizedProduct?.price) || 0;
        })();
        const tva = priceHT * 0.20;
        const priceTTC = priceHT + tva;

        const priceDiv = document.querySelector('.price-calc-display-admin');
        if (priceDiv && priceHT > 0) {
          priceDiv.innerHTML = `
            <div class="text-xs space-y-1.5">
              <div class="flex justify-between"><span>Prix HT:</span><span class="font-semibold">${priceHT.toFixed(2)}€</span></div>
              <div class="flex justify-between"><span>TVA (20%):</span><span class="text-orange-500">${tva.toFixed(2)}€</span></div>
              <div class="border-t border-gray-300 dark:border-white/10 pt-1.5 flex justify-between"><span class="font-semibold">Prix TTC:</span><span class="font-bold text-rose-500">${priceTTC.toFixed(2)}€</span></div>
            </div>
          `;
        }
      }, 200);
    };
    
    const handleViewProduct = (product) => {
      // Éviter un fetch qui peut renvoyer 500; utiliser les données présentes
      setProductFormMode('view');
      const normalizedProduct = normalizeProductForForm(product);
      setSelectedProduct(normalizedProduct);
      setProductSaleType(normalizedProduct?.sale_type || 'libre');
      const catName = resolveSelectedCategoryValue(normalizedProduct);
      setSelectedCategory(catName);
      // Extraire les IDs des utilisateurs autorisés
      const userIds = (normalizedProduct?.allowed_users || []).map(u => 
        typeof u === 'object' ? String(u.id_user || u.id) : String(u)
      );
      setAllowedUsers(userIds);
      setUploadedImages([]);
      setImagePreviews([]);
      setProductFiles([]);
      setProductModalOpen(true);
    };
    
    const handleDeleteProduct = async (product) => {
      if (window.confirm(`Voulez-vous vraiment supprimer le produit ${product.name} ?`)) {
        try {
          await api.delete(`/admin/products/${product.id_product || product.id}`);
          setProducts(products.filter(p => (p.id_product || p.id) !== (product.id_product || product.id)));
          toast.success('Produit supprimé avec succès');
          fetchProducts();
        } catch (error) {
          console.error('Erreur lors de la suppression:', error);
          toast.error('Erreur lors de la suppression du produit');
        }
      }
    };

    const handleActivateProduct = async (product) => {
      const id = product?.id_product || product?.id;
      if (!id) {
        toast.error('ID du produit manquant pour activer');
        console.error('Activation produit: ID manquant', product);
        return;
      }

      try {
        await api.patch(`/admin/products/${id}/activate`);
        toast.success('Produit activé');
        fetchProducts();
      } catch (error) {
        console.error('Erreur lors de l\'activation:', error);
        toast.error('Impossible d\'activer le produit');
      }
    };

    const handleToggleProductStatus = async (product) => {
      const newStatus = product.status === 'active' ? 'inactive' : 'active';
      try {
        const id = product.id_product || product.id;
        // Utilise la route PATCH /admin/products/{id} avec le champ status
        await api.patch(`/admin/products/${id}`, { status: newStatus });
        toast.success(`Produit ${newStatus === 'active' ? 'activé' : 'désactivé'} avec succès`);
        fetchProducts();
      } catch (error) {
        console.error('Erreur lors du basculement du statut:', error);
        toast.error('Erreur lors du basculement du statut');
      }
    };
    
    const MAX_IMAGE_COUNT = 8;
    const MAX_TOTAL_IMAGE_BYTES = 2.5 * 1024 * 1024;
    const MAX_PRODUCT_FILE_COUNT = 8;
    const MAX_SINGLE_PRODUCT_FILE_BYTES = 5 * 1024 * 1024;
    const MAX_TOTAL_PRODUCT_FILE_BYTES = 8 * 1024 * 1024;

    const handleImageUpload = async (e) => {
      e.stopPropagation();
      const existingImages = [...uploadedImages];
      const incomingFiles = Array.from(e.target.files || []);
      const remainingSlots = Math.max(0, MAX_IMAGE_COUNT - existingImages.length);
      const files = incomingFiles.slice(0, remainingSlots);
      if (files.length === 0) {
        if (incomingFiles.length > 0 && uploadedImages.length >= MAX_IMAGE_COUNT) {
          toast('Limite de 8 images atteinte.', { icon: '📸' });
        }
        e.target.value = '';
        return;
      }

      try {
        const options = {
          maxSizeMB: 0.35,
          maxWidthOrHeight: 1600,
          useWebWorker: true
        };

        const compressedFiles = await Promise.all(
          files.map((file) => imageCompression(file, options))
        );

        const previews = await Promise.all(
          compressedFiles.map(
            (file) =>
              new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.onerror = () => reject(new Error('Erreur lors de la lecture du fichier'));
                reader.readAsDataURL(file);
              })
          )
        );

        const totalCompressedBytes = [...existingImages, ...compressedFiles].reduce((sum, file) => sum + file.size, 0);

        if (totalCompressedBytes > MAX_TOTAL_IMAGE_BYTES) {
          toast.error('Les images restent trop lourdes apres compression. Reduis le nombre d\'images ou leur resolution.');
          e.target.value = '';
          return;
        }

        setUploadedImages((prev) => [...prev, ...compressedFiles]);
        setImagePreviews((prev) => [...prev, ...previews]);

        if (incomingFiles.length > remainingSlots) {
          toast('Seulement les emplacements restants ont été conservés.', { icon: '📸' });
        }

        if (totalCompressedBytes > 1024 * 1024) {
          toast('Les images ont ete compressees pour rester compatibles avec la limite serveur.', { icon: '🗜️' });
        }
        e.target.value = '';
      } catch (error) {
        console.error('Erreur handleImageUpload:', error);
        toast.error('Erreur lors du traitement des images');
        e.target.value = '';
      }
    };

    const handleRemoveUploadedImg = (indexToRemove) => {
      setUploadedImages((prev) => prev.filter((_, index) => index !== indexToRemove));
      setImagePreviews((prev) => prev.filter((_, index) => index !== indexToRemove));
    };

    const handleProductFilesChange = (e) => {
      const files = Array.from(e.target.files || []).slice(0, MAX_PRODUCT_FILE_COUNT);
      const tooLargeFile = files.find((file) => file.size > MAX_SINGLE_PRODUCT_FILE_BYTES);
      const totalBytes = files.reduce((sum, file) => sum + file.size, 0);

      if (tooLargeFile) {
        toast.error(`Le fichier ${tooLargeFile.name} depasse la limite de 5 Mo.`);
        setProductFiles([]);
        return;
      }

      if (totalBytes > MAX_TOTAL_PRODUCT_FILE_BYTES) {
        toast.error('Les fichiers telechargeables depassent 8 Mo au total.');
        setProductFiles([]);
        return;
      }

      setProductFiles(files);

      if ((e.target.files || []).length > MAX_PRODUCT_FILE_COUNT) {
        toast('Seulement les 8 premiers fichiers ont été conservés.', { icon: '📎' });
      }
    };
    
    // Handlers Créateurs
    const fetchCreators = async () => {
      try {
        const response = await api.get('/admin/creators');
        const raw = response.data?.creators || response.data?.data || response.data || [];
        const mapped = raw.map((c) => ({
          id_user: c.id_user ?? c.id,
          id: c.id_user ?? c.id,
          nickname: c.nickname ?? '',
          firstname: c.firstname ?? '',
          lastname: c.lastname ?? '',
          email: c.email ?? '',
          id_role: c.id_role ?? 2,
          role: c.role ?? c.role_name ?? 'Créateur',
          status: c.status ?? 'active',
          email_verified_at: c.email_verified_at,
          created_at: c.created_at,
        }));
        setCreators(mapped);
      } catch (error) {
        console.error('Erreur lors du chargement des créateurs:', error);
      }
    };
    
    const handleEditCreator = (creator) => {
      setCreatorFormMode('edit');
      setSelectedCreator(creator);
      setUploadedCreatorImage(null);
      setCreatorModalOpen(true);
    };
    
    const handleViewCreator = (creator) => {
      setCreatorFormMode('view');
      setSelectedCreator(creator);
      setCreatorModalOpen(true);
    };
    
    const handleDeleteCreator = async (creator) => {
      if (window.confirm(`Voulez-vous vraiment supprimer le créateur ${creator.nickname} ?`)) {
        try {
          await api.delete(`/admin/creators/${creator.id_user}`);
          toast.success('Créateur supprimé avec succès');
          fetchCreators();
          fetchCreatorsList();
        } catch (error) {
          console.error('Erreur lors de la suppression:', error);
          if (error.response?.status === 409) {
            toast.error('Impossible de supprimer ce créateur car il a des produits associés.');
          } else {
            toast.error('Erreur lors de la suppression du créateur');
          }
        }
      }
    };
    
    const handleSaveCreator = async (creatorData) => {
      try {
        const formData = new FormData();
        formData.append('nickname', creatorData.nickname);
        formData.append('firstname', creatorData.firstname);
        formData.append('lastname', creatorData.lastname);
        formData.append('email', creatorData.email);
        
        if (creatorData.password) {
          formData.append('password', creatorData.password);
        }
        
        if (uploadedCreatorImage) {
          formData.append('picture_user', uploadedCreatorImage);
        }
        
        if (creatorFormMode === 'create') {
          await api.post('/admin/creators', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Créateur créé avec succès!');
        } else {
          await api.put(`/admin/creators/${selectedCreator.id_user}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Créateur modifié avec succès');
        }
        
        setCreatorModalOpen(false);
        setUploadedCreatorImage(null);
        fetchCreators();
        fetchCreatorsList();
      } catch (error) {
        console.error('Erreur lors de la sauvegarde:', error);
        toast.error('Erreur lors de la sauvegarde: ' + (error.response?.data?.message || error.message));
      }
    };
    
    const handleCreatorImageUpload = (e) => {
      const file = e.target.files[0];
      if (file) {
        setUploadedCreatorImage(file);
      }
    };
    
    const handleSaveProduct = async (productData) => {
      try {
        // Vérifier l'authentification avant toute chose
        if (!isAuthenticated) {
          toast.error('Vous devez être connecté pour ajouter un produit', {
            duration: 4000,
            icon: '🔒',
            style: {
              background: isDark ? '#1f2937' : '#ffffff',
              color: isDark ? '#ffffff' : '#1f2937',
              border: '2px solid #ef4444',
            }
          });
          return;
        }

        // Validation côté client pour éviter un aller-retour inutile si l'image manque
        if (productFormMode === 'create' && uploadedImages.length === 0) {
          toast.error('Veuillez sélectionner entre 1 et 8 images avant de créer le produit');
          return;
        }

        // Validation des champs numériques
        const priceValue = Number(productData.price);
        const stockValue = Number(productData.stock);
        if (!Number.isFinite(priceValue) || priceValue < 0) {
          toast.error('Le prix doit être un nombre positif');
          return;
        }
        if (!Number.isInteger(stockValue) || stockValue < 0) {
          toast.error('Le stock doit être un entier positif (0 = illimité)');
          return;
        }

        // Validation: vérifier qu'UN utilisateur est sélectionné pour les ventes uniques
        if (productData.sale_type === 'unique' && allowedUsers.length === 0) {
          toast.error('Veuillez sélectionner UN utilisateur pour cette vente unique');
          return;
        }
        if (productData.sale_type === 'unique' && allowedUsers.length > 1) {
          toast.error('Une vente unique ne peut avoir qu\'UN SEUL acheteur');
          return;
        }


        // Déterminer le propriétaire du produit :
        // - Si on édite, garder les infos existantes (selectedProduct)
        // - Si on crée, mettre Krayne Agency
        let creatorType = 'krayne';
        let creatorName = 'Krayne Agency';
        let idUserCreator = null;
        if (productFormMode === 'edit' && selectedProduct) {
          creatorType = selectedProduct.creator_type || 'krayne';
          creatorName = selectedProduct.creator_name || 'Krayne Agency';
          idUserCreator = selectedProduct.id_user_creator || null;
        } else if (productFormMode === 'create') {
          // Si l'admin crée, c'est Krayne Agency
          creatorType = 'krayne';
          creatorName = 'Krayne Agency';
          idUserCreator = null;
        }

        const formData = new FormData();
        formData.append('name', productData.name);
        formData.append('category', productData.category);
        formData.append('price', priceValue);
        formData.append('stock', stockValue);
        formData.append('sale_type', productData.sale_type);
        formData.append('description', productData.description || '');
        formData.append('status', productData.status);
        formData.append('creator_type', creatorType);
        formData.append('creator_name', creatorName);
        if (idUserCreator) formData.append('id_user_creator', idUserCreator);

        // Produits en vente unique : limiter la visibilité à une liste d'utilisateurs
        if ((productData.sale_type || '').toLowerCase() === 'unique' && allowedUsers.length > 0) {
          allowedUsers.forEach((id) => formData.append('allowed_users[]', id));
        }
        
        if (uploadedImages.length > 0) {
          formData.append('image', uploadedImages[0]);
          uploadedImages.forEach((image) => {
            formData.append('images[]', image);
          });
        }

        if (productFiles.length > 0) {
          productFiles.forEach((file) => {
            formData.append('files[]', file);
          });
        }

        if (productFormMode === 'create') {
          await api.post('/admin/products', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
          toast.success('Produit créé avec succès!');
        } else {
          // Laravel ne supporte pas PUT avec multipart/form-data
          // On utilise POST avec _method=PUT
          formData.append('_method', 'PUT');
          await api.post(`/admin/products/${selectedProduct.id_product || selectedProduct.id}`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
          toast.success('Produit modifié avec succès');
        }
        
        setProductModalOpen(false);
        setUploadedImages([]);
        setImagePreviews([]);
        setProductFiles([]);
        fetchProducts();
      } catch (error) {
        console.error('Erreur lors de la sauvegarde:', error.response?.data || error);
        if (error?.response?.status === 413) {
          toast.error('Le formulaire est trop lourd pour le serveur. Reduis le nombre ou la taille des images et fichiers.');
          return;
        }
        const serverMessage = error.response?.data?.message || error.message;
        // Si le backend retourne des erreurs de validation, afficher la première
        const validationErrors = error.response?.data?.errors;
        const firstValidationError = validationErrors ? Object.values(validationErrors)[0]?.[0] : null;
        toast.error('Erreur lors de la sauvegarde: ' + (firstValidationError || serverMessage));
      }
    };

    const tabs = [
      { id: 'products', label: 'Produits', icon: Package },
      { id: 'users', label: 'Utilisateurs', icon: Users },
      { id: 'earnings', label: 'Gains par Créateur', icon: DollarSign },
      { id: 'categories', label: 'Catégories', icon: FolderKanban },
      { id: 'projects', label: 'Projets', icon: FolderKanban },
      { id: 'promos', label: 'Codes promo', icon: DollarSign },
      { id: 'newsletter', label: 'Newsletter', icon: Mail },
      { id: 'messages', label: 'Messages', icon: MessageSquare },
      { id: 'settings', label: 'Réglages', icon: Settings }
    ];

    const StatCard = ({ icon: Icon, title, value, trend, color }) => (
      <div className={`p-6 rounded-xl border ${isDark ? 'bg-[#0b0b0c] border-white/10' : 'bg-white border-gray-200'}`}>
        <div className="flex items-center justify-between mb-4">
          <div className={`p-3 rounded-lg ${color}`}>
            <Icon className="w-6 h-6" />
          </div>
          {trend && (
            <div className="flex items-center gap-1 text-sm text-rose-500">
              <TrendingUp className="w-4 h-4" />
              <span>{trend}</span>
            </div>
          )}
        </div>
        <h3 className={`text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{title}</h3>
        <p className="text-2xl font-bold">{value}</p>
      </div>
    );

    const productFormValues = normalizeProductForForm(selectedProduct) || normalizeProductForForm({
      creator_type: defaultCreatorType,
      id_user_creator: defaultCreatorId,
      creator_name: defaultCreatorName,
      stock: 1,
      status: 'active',
      sale_type: 'libre',
    });

    const resolvedCreatorType = productFormValues?.creator_type || defaultCreatorType;
    const resolvedCreatorId = productFormValues?.id_user_creator ?? defaultCreatorId;
    const resolvedCreatorName = productFormValues?.creator_name ?? defaultCreatorName;

    return (
        <div className={`flex-1 flex flex-col ${isDark ? 'bg-[#0f1114] text-white' : 'bg-slate-50 text-gray-800'}`}>
            <div className="container mx-auto px-4 py-8 max-w-7xl">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold mb-2">Administration</h1>
                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      Bienvenue, {user?.name || 'Admin'} • Dernière connexion : Aujourd'hui à 14:32
                    </p>
                </div>

                {/* Tabs Navigation */}
                <div className={`mb-6 rounded-3xl border p-3 ${isDark ? 'bg-[linear-gradient(180deg,rgba(255,255,255,0.03),rgba(255,255,255,0.01))] border-white/10 shadow-[0_10px_35px_rgba(0,0,0,0.18)]' : 'bg-[linear-gradient(180deg,#ffffff,#fbfbfd)] border-gray-200 shadow-[0_10px_30px_rgba(15,23,42,0.06)]'}`}>
                  <div className="flex flex-wrap items-center justify-center gap-2">
                  {tabs.map(tab => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`relative flex min-h-[48px] items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium leading-tight transition-all select-none ${
                          activeTab === tab.id 
                            ? 'bg-rose-500 text-white shadow-[0_10px_30px_rgba(244,63,94,0.28)] ring-2 ring-rose-500/20' 
                            : isDark ? 'bg-white/[0.03] text-gray-300 hover:bg-white/[0.06] hover:text-white border border-white/5' : 'bg-gray-50 text-gray-700 hover:bg-gray-100 hover:text-gray-900 border border-gray-200'
                        }`}
                        title={tab.label}
                      >
                        <Icon className="w-4 h-4" />
                        <span className="whitespace-nowrap">{tab.label}</span>
                        {tab.id === 'messages' && unreadMessagesCount > 0 && (
                          <span className="absolute -top-1 -right-1 bg-rose-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                            {unreadMessagesCount}
                          </span>
                        )}
                      </button>
                    );
                  })}
                  </div>
                </div>

                {/* Content */}

                {activeTab === 'users' && (
                  <div className="space-y-4">
                    {/* Sub-tabs pour Utilisateurs et Créateurs */}
                    <div className="flex gap-2 mb-6">
                      <button
                        onClick={() => setUserSubTab('users')}
                        className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                          userSubTab === 'users'
                            ? 'bg-rose-500 text-white'
                            : isDark
                            ? 'bg-white/5 hover:bg-white/10'
                            : 'bg-gray-100 hover:bg-gray-200'
                        }`}
                      >
                        Utilisateurs
                      </button>
                      <button
                        onClick={() => setUserSubTab('creators')}
                        className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                          userSubTab === 'creators'
                            ? 'bg-rose-500 text-white'
                            : isDark
                            ? 'bg-white/5 hover:bg-white/10'
                            : 'bg-gray-100 hover:bg-gray-200'
                        }`}
                      >
                        Créateurs
                      </button>
                    </div>

                    {/* Contenu Utilisateurs */}
                    {userSubTab === 'users' && (
                      <>
                        <div className="flex items-center justify-between mb-6">
                          <h2 className="text-xl font-semibold">Gestion des utilisateurs</h2>
                          <button 
                            onClick={handleCreateUser}
                            className="flex items-center gap-2 px-4 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-600 transition-colors text-sm font-medium"
                          >
                            <Plus className="w-4 h-4" />
                            Nouvel utilisateur
                          </button>
                        </div>
                        
                        <div className="mb-4">
                          <SearchBar 
                            value={searchUser}
                            onChange={setSearchUser}
                            placeholder="Rechercher un utilisateur..."
                          />
                        </div>
                        
                        <TableAdmin
                          columns={[
                            { key: 'id_user', label: 'ID' },
                            { key: 'nickname', label: 'Pseudo' },
                            { key: 'email', label: 'Email' },
                            { 
                              key: 'id_role', 
                              label: 'Rôle',
                              render: (role) => (
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  role === 3 ? 'bg-purple-500/20 text-purple-500' :
                                  role === 2 ? 'bg-blue-500/20 text-blue-500' :
                                  'bg-gray-500/20 text-gray-500'
                                }`}>
                                  {role === 3 ? 'Admin' : role === 2 ? 'Créateur' : 'Utilisateur'}
                                </span>
                              )
                            },
                            { 
                              key: 'email_verified_at', 
                              label: 'Vérification Email',
                              render: (verified) => (
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  verified ? 'bg-green-500/20 text-green-500' : 'bg-orange-500/20 text-orange-500'
                                }`}>
                                  {verified ? '✓ Vérifié' : '⏳ En attente'}
                                </span>
                              )
                            },
                            { 
                              key: 'status', 
                              label: 'Statut',
                              render: (status) => (
                                <span className={`px-2 py-1 rounded text-xs ${
                                  status === 'active' ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'
                                }`}>
                                  {status === 'active' ? 'Actif' : 'Inactif'}
                                </span>
                              )
                            },
                            { key: 'created_at', label: 'Date inscription' }
                          ]}
                          data={filteredUsers.slice((currentPageUsers - 1) * itemsPerPage, currentPageUsers * itemsPerPage)}
                          onView={handleViewUser}
                          onEdit={handleEditUser}
                          onDelete={handleDeleteUser}
                        />
                        
                        <Pagination
                          currentPage={currentPageUsers}
                          totalPages={totalPagesUsers}
                          onPageChange={setCurrentPageUsers}
                        />
                      </>
                    )}

                    {/* Contenu Créateurs */}
                    {userSubTab === 'creators' && (
                      <>
                        <div className="flex items-center justify-between mb-6">
                          <h2 className="text-xl font-semibold">Gestion des créateurs</h2>
                          <button 
                            onClick={() => {
                              setSelectedCreator(null);
                              setCreatorFormMode('create');
                              setCreatorModalOpen(true);
                            }}
                            className="flex items-center gap-2 px-4 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-600 transition-colors text-sm font-medium"
                          >
                            <Plus className="w-4 h-4" />
                            Ajouter un créateur
                          </button>
                        </div>
                        
                        <div className="mb-4">
                          <SearchBar 
                            value={searchCreator}
                            onChange={setSearchCreator}
                            placeholder="Rechercher un créateur..."
                          />
                        </div>
                        
                        <TableAdmin
                          columns={[
                            { key: 'id_user', label: 'ID' },
                            { key: 'nickname', label: 'Pseudo' },
                            { key: 'email', label: 'Email' },
                            { 
                              key: 'id_role', 
                              label: 'Rôle',
                              render: (role) => (
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  role === 3 ? 'bg-purple-500/20 text-purple-500' :
                                  role === 2 ? 'bg-blue-500/20 text-blue-500' :
                                  'bg-gray-500/20 text-gray-500'
                                }`}>
                                  {role === 3 ? 'Admin' : role === 2 ? 'Créateur' : 'Utilisateur'}
                                </span>
                              )
                            },
                            { 
                              key: 'email_verified_at', 
                              label: 'Vérification Email',
                              render: (verified) => (
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  verified ? 'bg-green-500/20 text-green-500' : 'bg-orange-500/20 text-orange-500'
                                }`}>
                                  {verified ? '✓ Vérifié' : '⏳ En attente'}
                                </span>
                              )
                            },
                            { 
                              key: 'status', 
                              label: 'Statut',
                              render: (status) => (
                                <span className={`px-2 py-1 rounded text-xs ${
                                  status === 'active' ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'
                                }`}>
                                  {status === 'active' ? 'Actif' : 'Inactif'}
                                </span>
                              )
                            }
                          ]}
                          data={filteredCreators.slice((currentPageCreators - 1) * itemsPerPage, currentPageCreators * itemsPerPage)}
                          onView={handleViewCreator}
                          onEdit={handleEditCreator}
                          onDelete={handleDeleteCreator}
                        />
                        
                        <Pagination
                          currentPage={currentPageCreators}
                          totalPages={totalPagesCreators}
                          onPageChange={setCurrentPageCreators}
                        />
                      </>
                    )}
                  </div>
                )}

                {activeTab === 'orders' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-xl font-semibold">Gestion des commandes</h2>
                      <div className="flex items-center gap-2">
                        <button className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-white/5 transition-colors text-sm font-medium">
                          <Filter className="w-4 h-4" />
                          Filtrer
                        </button>
                        <button className="flex items-center gap-2 px-4 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-600 transition-colors text-sm font-medium">
                          <Download className="w-4 h-4" />
                          Exporter
                        </button>
                      </div>
                    </div>
                    
                    <div className="mb-4">
                      <SearchBar 
                        value={searchOrder}
                        onChange={setSearchOrder}
                        placeholder="Rechercher une commande..."
                      />
                    </div>
                    
                    <TableAdmin
                      columns={[
                        { key: 'order_id', label: 'N° Commande' },
                        { key: 'client', label: 'Client' },
                        { key: 'service', label: 'Service' },
                        { 
                          key: 'amount', 
                          label: 'Montant',
                          render: (amount) => `${amount}€`
                        },
                        { 
                          key: 'status', 
                          label: 'Statut',
                          render: (status) => {
                            const normalized = String(status || '').toLowerCase();
                            const badgeClass =
                              normalized === 'payee' || normalized === 'payé' || normalized === 'complété'
                                ? 'bg-green-500/20 text-green-500'
                                : normalized === 'en attente' || normalized === 'en cours'
                                ? 'bg-blue-500/20 text-blue-500'
                                : normalized === 'echoue' || normalized === 'échoué' || normalized === 'annule' || normalized === 'annulé'
                                ? 'bg-red-500/20 text-red-500'
                                : 'bg-rose-500/20 text-rose-500';

                            return (
                              <span className={`px-2 py-1 rounded text-xs ${badgeClass}`}>
                                {status}
                              </span>
                            );
                          }
                        },
                        { key: 'date', label: 'Date' }
                      ]}
                      data={filteredOrders.slice((currentPageOrders - 1) * itemsPerPage, currentPageOrders * itemsPerPage)}
                      onView={handleViewOrder}
                      onEdit={null}
                      onDelete={null}
                    />
                    
                    <Pagination
                      currentPage={currentPageOrders}
                      totalPages={totalPagesOrders}
                      onPageChange={setCurrentPageOrders}
                    />
                  </div>
                )}

                {activeTab === 'earnings' && (
                  <div className="space-y-4">
                    {(() => {
                      const totalSales = filteredEarnings.reduce((sum, earning) => sum + Number(earning.total_sales || 0), 0);
                      const totalCreatorShare = filteredEarnings.reduce((sum, earning) => sum + Number(earning.creator_share || 0), 0);
                      const totalAdminShare = filteredEarnings.reduce((sum, earning) => sum + Number(earning.admin_share || 0), 0);
                      const totalProductsSold = filteredEarnings.reduce((sum, earning) => sum + Number(earning.products_sold || 0), 0);
                      const averageBasket = totalProductsSold > 0 ? totalSales / totalProductsSold : 0;

                      return (
                        <>
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h2 className="text-xl font-semibold">Gains par Créateur</h2>
                        <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          Vue claire des ventes encaissées, montants à reverser et commission Krayne Agency.
                        </p>
                      </div>
                      <button 
                        onClick={() => fetchEarnings(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-600 transition-colors text-sm font-medium"
                      >
                        <Download className="w-4 h-4" />
                        Rafraîchir
                      </button>
                    </div>
                    
                    <div className="mb-4">
                      <SearchBar 
                        value={searchEarnings}
                        onChange={setSearchEarnings}
                        placeholder="Rechercher un créateur..."
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                      <div className={`p-5 rounded-2xl border ${isDark ? 'bg-[#0b0b0c] border-white/10' : 'bg-white border-gray-200'}`}>
                        <div className={`text-sm mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>CA créateurs</div>
                        <div className="text-2xl font-bold">{formatCurrency(totalSales)}</div>
                        <div className={`text-xs mt-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{filteredEarnings.length} créateur(s) avec ventes</div>
                      </div>

                      <div className={`p-5 rounded-2xl border ${isDark ? 'bg-[#0b0b0c] border-white/10' : 'bg-white border-gray-200'}`}>
                        <div className={`text-sm mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>À reverser aux créateurs</div>
                        <div className="text-2xl font-bold text-emerald-500">{formatCurrency(totalCreatorShare)}</div>
                        <div className={`text-xs mt-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Montant total prévu pour les créateurs</div>
                      </div>

                      <div className={`p-5 rounded-2xl border ${isDark ? 'bg-[#0b0b0c] border-white/10' : 'bg-white border-gray-200'}`}>
                        <div className={`text-sm mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Commission Krayne Agency</div>
                        <div className="text-2xl font-bold text-rose-500">{formatCurrency(totalAdminShare)}</div>
                        <div className={`text-xs mt-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Basée sur la part plateforme enregistrée</div>
                      </div>

                      <div className={`p-5 rounded-2xl border ${isDark ? 'bg-[#0b0b0c] border-white/10' : 'bg-white border-gray-200'}`}>
                        <div className={`text-sm mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Produits vendus</div>
                        <div className="text-2xl font-bold text-blue-500">{totalProductsSold}</div>
                        <div className={`text-xs mt-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Panier moyen : {formatCurrency(averageBasket)}</div>
                      </div>
                    </div>

                    <div className={`rounded-2xl border p-4 ${isDark ? 'bg-blue-500/5 border-blue-500/20 text-blue-100' : 'bg-blue-50 border-blue-200 text-blue-900'}`}>
                      <div className="text-sm font-medium">Lecture simple</div>
                      <div className={`text-sm mt-1 ${isDark ? 'text-blue-100/80' : 'text-blue-800'}`}>
                        Cette vue affiche uniquement le chiffre d’affaires, la part créateur, la commission plateforme et les volumes vendus. Les charges externes comme l’URSSAF ne sont pas affichées ici.
                      </div>
                    </div>
                    
                    {/* Tableau des gains */}
                    <div className={`overflow-x-auto rounded-xl border ${isDark ? 'bg-[#0b0b0c] border-white/10' : 'bg-white border-gray-200'}`}>
                      <table className="w-full">
                        <thead>
                          <tr className={`border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                            <th className={`text-left p-4 text-sm font-semibold ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Créateur</th>
                            <th className={`text-left p-4 text-sm font-semibold ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Email</th>
                            <th className={`text-right p-4 text-sm font-semibold ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>CA généré</th>
                            <th className={`text-right p-4 text-sm font-semibold ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>À reverser</th>
                            <th className={`text-right p-4 text-sm font-semibold ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Commission</th>
                            <th className={`text-center p-4 text-sm font-semibold ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Produits Vendus</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredEarnings.slice((currentPageEarnings - 1) * itemsPerPage, currentPageEarnings * itemsPerPage).map((earning, idx) => (
                            <tr key={earning.creator_id} className={`border-b ${isDark ? 'border-white/5 hover:bg-white/5' : 'border-gray-100 hover:bg-gray-50'} transition-colors`}>
                              <td className="p-4">
                                <div className="font-medium">{earning.creator_name}</div>
                                <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>ID: {earning.creator_id}</div>
                              </td>
                              <td className="p-4 text-sm">{earning.creator_email}</td>
                              <td className="p-4 text-right font-semibold">{formatCurrency(earning.total_sales)}</td>
                              <td className="p-4 text-right text-green-500 font-medium">{formatCurrency(earning.creator_share)}</td>
                              <td className="p-4 text-right text-rose-500 font-medium">{formatCurrency(earning.admin_share)}</td>
                              <td className="p-4 text-center">
                                <span className={`px-2 py-1 rounded text-xs font-medium ${isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-600'}`}>
                                  {earning.products_sold}
                                </span>
                              </td>
                            </tr>
                          ))}
                          {filteredEarnings.length === 0 && (
                            <tr>
                              <td colSpan="6" className="p-8 text-center text-gray-500">
                                Aucune vente enregistrée
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                    
                    {filteredEarnings.length > 0 && (
                      <Pagination
                        currentPage={currentPageEarnings}
                        totalPages={totalPagesEarnings}
                        onPageChange={setCurrentPageEarnings}
                      />
                    )}
                        </>
                      );
                    })()}
                  </div>
                )}

                {activeTab === 'products' && (
                  <div className="space-y-4">
                    {productsError && (
                      <div className={`p-4 border rounded-xl flex items-start gap-3 shadow-sm ${isDark ? 'bg-gradient-to-r from-rose-500/10 via-orange-500/10 to-amber-500/10 border-white/10' : 'bg-gradient-to-r from-rose-50 via-orange-50 to-amber-50 border-amber-100'}`}>
                        <div className="mt-0.5">
                          <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 ${isDark ? 'text-rose-300' : 'text-rose-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 9v3m0 3h.01M10.29 3.86l-7 12A1 1 0 004 18h16a1 1 0 00.87-1.5l-7-12a1 1 0 00-1.74 0z" />
                          </svg>
                        </div>
                        <div className="flex-1 space-y-1">
                          <p className="font-semibold text-sm">Impossible de charger les produits</p>
                          <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{productsError}</p>
                          <div className="flex gap-2 pt-2">
                            <button
                              type="button"
                              onClick={fetchProducts}
                              className={`px-3 py-1.5 rounded-lg text-sm font-medium shadow-sm ${isDark ? 'bg-rose-500/80 hover:bg-rose-500 text-white' : 'bg-rose-500 hover:bg-rose-600 text-white'}`}
                            >
                              Réessayer
                            </button>
                            <button
                              type="button"
                              onClick={() => setProductsError(null)}
                              className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${isDark ? 'border-white/10 text-gray-200 hover:bg-white/5' : 'border-gray-200 text-gray-700 hover:bg-white'}`}
                            >
                              Masquer
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-xl font-semibold">Gestion des produits</h2>
                      <button 
                        onClick={handleCreateProduct}
                        className="flex items-center gap-2 px-4 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-600 transition-colors text-sm font-medium"
                      >
                        <Plus className="w-4 h-4" />
                        Nouveau produit
                      </button>
                    </div>
                    
                    {/* Onglets de statut avec compteurs */}
                    <div className="hide-scrollbar flex gap-2 mb-4 overflow-x-auto">
                      {[
                        { key: 'all', label: 'Tous', count: products.length },
                        { key: 'pending', label: 'En attente', count: products.filter(p => p.status === 'pending').length },
                        { key: 'active', label: 'Actifs', count: products.filter(p => p.status === 'active').length },
                        { key: 'inactive', label: 'Inactifs', count: products.filter(p => p.status === 'inactive').length },
                      ].map(tab => (
                        <button
                          key={tab.key}
                          onClick={() => {
                            setProductStatusTab(tab.key);
                            setCurrentPageProducts(1);
                          }}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                            productStatusTab === tab.key
                              ? 'bg-rose-500 text-white shadow-md'
                              : isDark
                              ? 'bg-white/5 border border-white/10 text-gray-100 hover:bg-white/10'
                              : 'bg-gray-100 border border-gray-200 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          <span>{tab.label}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                            productStatusTab === tab.key
                              ? 'bg-white/20 text-white'
                              : isDark
                              ? 'bg-white/10 text-gray-300'
                              : 'bg-white text-gray-600'
                          }`}>
                            {tab.count}
                          </span>
                        </button>
                      ))}
                    </div>
                    
                    <div className="mb-4">
                      <SearchBar 
                        value={searchProduct}
                        onChange={setSearchProduct}
                        placeholder="Rechercher un produit..."
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                      <select
                        value={productFilters.creatorId}
                        onChange={(e) => setProductFilters(prev => ({ ...prev, creatorId: e.target.value }))}
                        className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-[#0f1114] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                      >
                        <option value="">Tous les créateurs</option>
                        {creatorsList.map((c) => (
                          <option key={c.id_user} value={c.id_user}>
                            {c.nickname || c.fullname || c.email}
                          </option>
                        ))}
                      </select>

                      <select
                        value={productFilters.creatorType}
                        onChange={(e) => setProductFilters(prev => ({ ...prev, creatorType: e.target.value }))}
                        className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-[#0f1114] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                      >
                        <option value="">Type de créateur</option>
                        <option value="krayne">Krayne</option>
                        <option value="external">Externe</option>
                      </select>

                      <div className="flex gap-2">
                        <select
                          value={productFilters.saleType}
                          onChange={(e) => setProductFilters(prev => ({ ...prev, saleType: e.target.value }))}
                          className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-[#0f1114] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                        >
                          <option value="">Type de vente</option>
                          <option value="libre">Libre</option>
                          <option value="unique">Unique</option>
                        </select>
                        <button
                          type="button"
                          onClick={() => setProductFilters({ saleType: '', status: '', creatorType: '', creatorId: '' })}
                          className={`px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${isDark ? 'bg-white/5 border border-white/10 text-gray-100 hover:bg-white/10' : 'bg-gray-100 border border-gray-200 text-gray-700 hover:bg-gray-200'}`}
                        >
                          Réinitialiser
                        </button>
                      </div>
                    </div>
                    
                    <TableAdmin
                      columns={[
                        { key: 'id', label: 'ID' },
                        { key: 'name', label: 'Nom' },
                        { 
                          key: 'category', 
                          label: 'Catégorie',
                          render: (cat) => (
                            <span className={`px-2 py-1 rounded text-xs ${isDark ? 'bg-white/10' : 'bg-gray-100'}`}>
                              {cat}
                            </span>
                          )
                        },
                        { 
                          key: 'creator_type', 
                          label: 'Créateur',
                          render: (type, product) => (
                            <div>
                              <span className={`px-2 py-1 rounded text-xs ${
                                type === 'krayne' ? 'bg-rose-500/20 text-rose-500' : 'bg-orange-500/20 text-orange-500'
                              }`}>
                                {type === 'krayne' ? 'Krayne' : 'Externe'}
                              </span>
                              {product.creator_name && (
                                <div className="text-xs text-gray-500 mt-1">{product.creator_name}</div>
                              )}
                            </div>
                          )
                        },
                        { 
                          key: 'price', 
                          label: 'Prix HT',
                          render: (price) => `${price}€`
                        },
                        { 
                          key: 'stock', 
                          label: 'Stock',
                          render: (stock) => stock === 0 ? 'Illimité' : stock
                        },
                        { 
                          key: 'sale_type', 
                          label: 'Type',
                          render: (type) => (
                            <span className={`px-2 py-1 rounded text-xs ${
                              type === 'unique' ? 'bg-purple-500/20 text-purple-500' : 'bg-blue-500/20 text-blue-500'
                            }`}>
                              {type === 'unique' ? 'Unique' : 'Libre'}
                            </span>
                          )
                        },
                        { 
                          key: 'status', 
                          label: 'Statut',
                          render: (status) => (
                            <span className={`px-2 py-1 rounded text-xs ${
                              status === 'active' ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'
                            }`}>
                              {status === 'active' ? 'Actif' : 'Inactif'}
                            </span>
                          )
                        },
                        {
                          key: 'actions',
                          label: 'Actions',
                          render: (_, product) => {
                            const isPending = product.status === 'pending';
                            const isActive = product.status === 'active';
                            const powerClass = isActive
                              ? isDark ? 'hover:bg-green-500/10 text-green-500' : 'hover:bg-green-50 text-green-600'
                              : isPending
                                ? isDark ? 'hover:bg-green-500/10 text-green-500' : 'hover:bg-green-50 text-green-600'
                                : isDark ? 'hover:bg-orange-500/10 text-orange-500' : 'hover:bg-orange-50 text-orange-600';

                            const handlePowerClick = () => {
                              if (isPending) return handleActivateProduct(product);
                              return handleToggleProductStatus(product);
                            };

                            return (
                              <div className="flex items-center justify-end gap-2 w-full">
                                <button
                                  onClick={() => handleViewProduct(product)}
                                  className={`p-2 rounded transition ${isDark ? 'hover:bg-blue-500/10 text-blue-500' : 'hover:bg-blue-50 text-blue-600'}`}
                                  title="Voir"
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleEditProduct(product)}
                                  className={`p-2 rounded transition ${isDark ? 'hover:bg-yellow-500/10 text-yellow-400' : 'hover:bg-yellow-50 text-yellow-600'}`}
                                  title="Modifier"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={handlePowerClick}
                                  className={`p-2 rounded transition ${powerClass}`}
                                  title={isPending ? 'Activer' : isActive ? 'Désactiver' : 'Activer'}
                                >
                                  <Power className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteProduct(product)}
                                  className={`p-2 rounded transition ${isDark ? 'hover:bg-red-500/10 text-red-500' : 'hover:bg-red-50 text-red-600'}`}
                                  title="Supprimer"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            );
                          }
                        },
                        { key: 'created_at', label: 'Date création' }
                      ]}
                      data={filteredProducts.slice((currentPageProducts - 1) * itemsPerPage, currentPageProducts * itemsPerPage)}
                      onView={handleViewProduct}
                      onEdit={handleEditProduct}
                      onDelete={handleDeleteProduct}
                      actions={false}
                    />
                    
                    <Pagination
                      currentPage={currentPageProducts}
                      totalPages={totalPagesProducts}
                      onPageChange={setCurrentPageProducts}
                    />
                  </div>
                )}

                {activeTab === 'messages' && (
                  <MessagingAdmin isDark={isDark} />
                )}

                {activeTab === 'settings' && (
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-xl font-semibold">Maintenance & accès</h2>
                      <p className={`mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        Activez le mode maintenance du site et bloquez temporairement certains rôles. Les administrateurs gardent l’accès.
                      </p>
                    </div>

                    {siteSettingsLoading ? (
                      <div className={`rounded-xl border p-6 ${isDark ? 'border-white/10 bg-[#0b0b0c]' : 'border-gray-200 bg-white'}`}>
                        Chargement des paramètres...
                      </div>
                    ) : (
                      <div className="grid gap-6 lg:grid-cols-2">
                        <div className={`rounded-2xl border p-6 ${isDark ? 'border-white/10 bg-[#0b0b0c]' : 'border-gray-200 bg-white'}`}>
                          <div className="flex items-start justify-between gap-4">
                            <div>
                              <h3 className="text-lg font-semibold">Mode maintenance</h3>
                              <p className={`mt-2 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                Affiche un écran de maintenance à tout le site, sauf aux administrateurs connectés.
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => setSiteSettings((prev) => ({ ...prev, maintenance_mode: !prev.maintenance_mode }))}
                              aria-pressed={siteSettings.maintenance_mode}
                              className={`inline-flex h-8 w-16 items-center rounded-full px-1 transition-all duration-200 ${siteSettings.maintenance_mode ? 'justify-end bg-rose-500 shadow-[0_8px_24px_rgba(244,63,94,0.3)]' : `justify-start ${isDark ? 'bg-white/10' : 'bg-gray-300'}`}`}
                            >
                              <span
                                className="inline-block h-6 w-6 rounded-full bg-white shadow-md transition-all duration-200"
                              />
                            </button>
                          </div>
                          <div className={`mt-4 rounded-xl px-4 py-3 text-sm ${siteSettings.maintenance_mode ? 'bg-rose-500/10 text-rose-400 border border-rose-500/20' : isDark ? 'bg-white/5 text-gray-300 border border-white/10' : 'bg-gray-50 text-gray-600 border border-gray-200'}`}>
                            {siteSettings.maintenance_mode ? 'Le site public affichera la page de maintenance.' : 'Le site est actuellement accessible normalement.'}
                          </div>
                        </div>

                        <div className={`rounded-2xl border p-6 ${isDark ? 'border-white/10 bg-[#0b0b0c]' : 'border-gray-200 bg-white'}`}>
                          <h3 className="text-lg font-semibold">Bloquer un rôle</h3>
                          <p className={`mt-2 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                            Les rôles cochés verront un écran de suspension temporaire à la place du site.
                          </p>

                          <div className="mt-5 space-y-3">
                            {[
                              { id: 1, label: 'Utilisateurs' },
                              { id: 2, label: 'Créateurs' },
                            ].map((roleOption) => {
                              const checked = (siteSettings.blocked_roles || []).map((role) => Number(role)).includes(roleOption.id);

                              return (
                                <label
                                  key={roleOption.id}
                                  className={`flex items-center justify-between rounded-xl border px-4 py-3 ${isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-gray-50'}`}
                                >
                                  <span className="font-medium">{roleOption.label}</span>
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => handleToggleBlockedRole(roleOption.id)}
                                    className="h-4 w-4 rounded border-gray-300 text-rose-500 focus:ring-rose-500"
                                  />
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={handleSaveSiteSettings}
                        disabled={siteSettingsSaving || siteSettingsLoading}
                        className={`rounded-xl px-5 py-3 text-sm font-semibold text-white ${siteSettingsSaving || siteSettingsLoading ? 'bg-gray-400 cursor-not-allowed' : 'bg-rose-500 hover:bg-rose-600'}`}
                      >
                        {siteSettingsSaving ? 'Enregistrement...' : 'Enregistrer les réglages'}
                      </button>
                    </div>
                  </div>
                )}

                {activeTab === 'categories' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-xl font-semibold">Gestion des catégories</h2>
                      <button 
                        onClick={() => {
                          setSelectedCategoryData(null);
                          setCategoryFormMode('create');
                          setCategoryModalOpen(true);
                        }}
                        className="flex items-center gap-2 px-4 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-600 transition-colors text-sm font-medium"
                      >
                        <Plus className="w-4 h-4" />
                        Ajouter une catégorie
                      </button>
                    </div>
                    
                    <TableAdmin
                      columns={[
                        { key: 'name', label: 'Nom' },
                        { key: 'slug', label: 'Slug' },
                        { 
                          key: 'product_count', 
                          label: 'Produits',
                          render: (_, cat) => cat.products?.length || 0
                        },
                        { 
                          key: 'created_at', 
                          label: 'Créée le',
                          render: (date) => date ? new Date(date).toLocaleDateString('fr-FR') : '-'
                        }
                      ]}
                      data={categories}
                      onEdit={(cat) => {
                        setSelectedCategoryData(cat);
                        setCategoryFormMode('edit');
                        setCategoryModalOpen(true);
                      }}
                      onDelete={(cat) => handleDeleteCategory(cat.id_category)}
                    />
                  </div>
                )}

                {activeTab === 'promos' && (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-xl font-semibold">Codes promo</h2>
                        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          Activez un code et la newsletter est notifiee.
                        </p>
                      </div>
                    </div>

                    <div className={`p-5 rounded-xl border ${isDark ? 'bg-[#0b0b0c] border-white/10' : 'bg-white border-gray-200'}`}>
                      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                        <input
                          value={promoForm.code}
                          onChange={(e) => setPromoForm((prev) => ({ ...prev, code: e.target.value }))}
                          placeholder="CODE10"
                          className={`px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-[#0f1114] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                        />
                        <input
                          type="number"
                          value={promoForm.discount}
                          onChange={(e) => setPromoForm((prev) => ({ ...prev, discount: e.target.value }))}
                          placeholder="10"
                          className={`px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-[#0f1114] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                        />
                        <input
                          type="date"
                          value={promoForm.start_date}
                          onChange={(e) => setPromoForm((prev) => ({ ...prev, start_date: e.target.value }))}
                          className={`px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-[#0f1114] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                        />
                        <input
                          type="date"
                          value={promoForm.end_date}
                          onChange={(e) => setPromoForm((prev) => ({ ...prev, end_date: e.target.value }))}
                          className={`px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-[#0f1114] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                        />
                        <select
                          value={promoForm.id_product}
                          onChange={(e) => setPromoForm((prev) => ({ ...prev, id_product: e.target.value }))}
                          className={`px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-[#0f1114] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                        >
                          <option value="">Tous les produits</option>
                          {products.map((p) => (
                            <option key={p.id_product || p.id} value={p.id_product || p.id}>
                              {p.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="mt-4 flex justify-end">
                        <button
                          onClick={handleCreatePromo}
                          className="px-4 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-600"
                        >
                          Creer le code
                        </button>
                      </div>
                    </div>

                    <div className={`overflow-x-auto rounded-xl border ${isDark ? 'bg-[#0b0b0c] border-white/10' : 'bg-white border-gray-200'}`}>
                      <table className="w-full">
                        <thead className={`${isDark ? 'bg-[#0f1114]' : 'bg-gray-50'}`}>
                          <tr>
                            <th className={`px-4 py-3 text-left text-xs font-semibold uppercase ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Code</th>
                            <th className={`px-4 py-3 text-left text-xs font-semibold uppercase ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Reduction</th>
                            <th className={`px-4 py-3 text-left text-xs font-semibold uppercase ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Periode</th>
                            <th className={`px-4 py-3 text-left text-xs font-semibold uppercase ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Statut</th>
                            <th className={`px-4 py-3 text-right text-xs font-semibold uppercase ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Action</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                          {promoCodes.length === 0 && (
                            <tr>
                              <td colSpan="5" className="px-4 py-8 text-center text-sm text-gray-500">
                                Aucun code promo
                              </td>
                            </tr>
                          )}
                          {promoCodes.map((promo) => {
                            const id = promo.id_promo_code || promo.id;
                            const pulse = promoFlashId === id ? 'animate-pulse ring-2 ring-rose-400/50' : '';
                            const isActive = !!promo.is_active;
                            return (
                              <tr
                                key={id}
                                className={`${isActive
                                  ? (isDark ? 'bg-emerald-500/10' : 'bg-emerald-50')
                                  : ''} ${isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'} transition-colors ${pulse}`}
                              >
                                <td className={`px-4 py-3 text-sm tracking-[0.2em] ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{promo.code}</td>
                                <td className={`px-4 py-3 text-sm ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{Number(promo.discount).toFixed(0)}%</td>
                                <td className={`px-4 py-3 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                  {promo.start_date ? new Date(promo.start_date).toLocaleDateString('fr-FR') : '-'}
                                  {' → '}
                                  {promo.end_date ? new Date(promo.end_date).toLocaleDateString('fr-FR') : '-'}
                                </td>
                                <td className="px-4 py-3">
                                  <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wide ${isActive
                                    ? 'bg-emerald-500/20 text-emerald-400'
                                    : 'bg-gray-500/20 text-gray-400'}`}>
                                    <span className={`h-2.5 w-2.5 rounded-full ${isActive ? 'bg-emerald-400' : 'bg-gray-400'}`} />
                                    {isActive ? 'Actif' : 'Inactif'}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-right">
                                  <div className="flex items-center justify-end gap-2">
                                    <button
                                      onClick={() => handleTogglePromo(promo)}
                                      className={`px-3 py-1.5 rounded-lg text-xs font-medium ${isActive ? 'bg-gray-500/20 text-gray-200' : 'bg-rose-500 text-white'} hover:opacity-90`}
                                    >
                                      {isActive ? 'Desactiver' : 'Activer'}
                                    </button>
                                    <button
                                      onClick={() => handleDeletePromo(promo)}
                                      className={`px-3 py-1.5 rounded-lg text-xs font-medium ${isDark ? 'bg-red-500/20 text-red-300' : 'bg-red-50 text-red-600'} hover:opacity-90`}
                                    >
                                      Supprimer
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {activeTab === 'newsletter' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h2 className="text-xl font-semibold">Newsletter</h2>
                      <button
                        onClick={fetchNewsletter}
                        className="px-4 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-600"
                      >
                        Rafraichir
                      </button>
                    </div>

                    <SearchBar
                      value={newsletterSearch}
                      onChange={setNewsletterSearch}
                      placeholder="Rechercher un email..."
                    />

                    <TableAdmin
                      columns={[
                        { key: 'email', label: 'Email' },
                        {
                          key: 'is_active',
                          label: 'Statut',
                          render: (active) => (
                            <span className={`px-2 py-1 rounded text-xs font-medium ${active ? 'bg-green-500/20 text-green-500' : 'bg-gray-500/20 text-gray-500'}`}>
                              {active ? 'Actif' : 'Inactif'}
                            </span>
                          )
                        },
                        {
                          key: 'subscribed_at',
                          label: 'Inscription',
                          render: (date) => date ? new Date(date).toLocaleDateString('fr-FR') : '-'
                        }
                      ]}
                      data={newsletterSubscribers.filter((s) => (s.email || '').toLowerCase().includes(newsletterSearch.toLowerCase()))}
                      onView={null}
                      onEdit={handleToggleNewsletter}
                      onDelete={handleDeleteNewsletter}
                    />
                  </div>
                )}


                {activeTab === 'projects' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-xl font-semibold">Gestion des projets</h2>
                      <button 
                        onClick={handleCreateProject}
                        className="flex items-center gap-2 px-4 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-600 transition-colors text-sm font-medium"
                      >
                        <Plus className="w-4 h-4" />
                        Créer un projet
                      </button>
                    </div>
                    
                    <div className="mb-4">
                      <SearchBar 
                        value={searchProject}
                        onChange={setSearchProject}
                        placeholder="Rechercher un projet..."
                      />
                    </div>
                    
                    <TableAdmin
                      columns={[
                        { key: 'id_project', label: 'ID' },
                        { key: 'name', label: 'Nom' },
                        { key: 'user_name', label: 'Client' },
                        { key: 'type', label: 'Type' },
                        { 
                          key: 'status', 
                          label: 'Statut',
                          render: (status) => (
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              status === 'En cours' ? 'bg-blue-500/20 text-blue-500' :
                              status === 'Terminé' ? 'bg-green-500/20 text-green-500' :
                              'bg-yellow-500/20 text-yellow-500'
                            }`}>
                              {status}
                            </span>
                          )
                        },
                        { 
                          key: 'progress', 
                          label: 'Progression',
                          render: (progress) => `${progress || 0}%`
                        }
                      ]}
                      data={filteredProjects.slice((currentPageProjects - 1) * itemsPerPage, currentPageProjects * itemsPerPage)}
                      onView={handleViewProject}
                      onEdit={handleEditProject}
                      onDelete={handleDeleteProject}
                    />
                    
                    <Pagination
                      currentPage={currentPageProjects}
                      totalPages={totalPagesProjects}
                      onPageChange={setCurrentPageProjects}
                    />
                  </div>
                )}

            </div>
            
            {/* Modals */}
            
            {/* Modal Utilisateur */}
            <ModalAdmin
              isOpen={userModalOpen}
              onClose={() => setUserModalOpen(false)}
              title={userFormMode === 'create' ? 'Créer un utilisateur' : userFormMode === 'edit' ? 'Modifier l\'utilisateur' : 'Détails de l\'utilisateur'}
              size="md"
            >
              {userFormMode === 'view' && selectedUser ? (
                <div className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Pseudo</label>
                    <p className={isDark ? 'text-white' : 'text-gray-800'}>{selectedUser.nickname}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Prénom</label>
                      <p className={isDark ? 'text-white' : 'text-gray-800'}>{selectedUser.firstname}</p>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Nom</label>
                      <p className={isDark ? 'text-white' : 'text-gray-800'}>{selectedUser.lastname}</p>
                    </div>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Email</label>
                    <p className={isDark ? 'text-white' : 'text-gray-800'}>{selectedUser.email}</p>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Rôle</label>
                    <p className={isDark ? 'text-white' : 'text-gray-800'}>{selectedUser.role || (selectedUser.id_role === 3 ? 'Admin' : selectedUser.id_role === 2 ? 'Créateur' : 'Utilisateur')}</p>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Vérification email</label>
                    <p className={selectedUser.email_verified_at ? 'text-green-500' : 'text-orange-500'}>
                      {selectedUser.email_verified_at ? '✓ Vérifié' : '⏳ En attente'}
                    </p>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Statut du compte</label>
                    <div className="flex items-center justify-between">
                      <p className={selectedUser.status === 'active' ? 'text-green-500' : 'text-red-500'}>
                        {selectedUser.status === 'active' ? '✓ Actif' : '✕ Inactif'}
                      </p>
                      <button
                        onClick={async () => {
                          const newStatus = selectedUser.status === 'active' ? 'inactive' : 'active';
                          try {
                            await api.put(`/admin/users/${selectedUser.id_user || selectedUser.id}`, {
                              ...selectedUser,
                              status: newStatus
                            });
                            toast.success(`Utilisateur ${newStatus === 'active' ? 'activé' : 'désactivé'}`);
                            setSelectedUser({ ...selectedUser, status: newStatus });
                            fetchUsers();
                          } catch (error) {
                            toast.error('Erreur lors du changement de statut');
                          }
                        }}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                          selectedUser.status === 'active'
                            ? 'bg-red-500/10 text-red-500 hover:bg-red-500/20'
                            : 'bg-green-500/10 text-green-500 hover:bg-green-500/20'
                        }`}
                      >
                        {selectedUser.status === 'active' ? 'Désactiver' : 'Activer'}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Date d'inscription</label>
                    <p className={isDark ? 'text-white' : 'text-gray-800'}>{new Date(selectedUser.created_at).toLocaleDateString('fr-FR')}</p>
                  </div>
                </div>
              ) : (
                <form onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target);
                  const userData = {
                    nickname: formData.get('nickname'),
                    firstname: formData.get('firstname'),
                    lastname: formData.get('lastname'),
                    email: formData.get('email'),
                    id_role: parseInt(formData.get('role')),
                    status: formData.get('status'),
                  };
                  
                  // Ajouter le mot de passe uniquement si fourni
                  const password = formData.get('password');
                  if (password && password.trim()) {
                    userData.password = password;
                  }
                  
                  handleSaveUser(userData);
                }} className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Pseudo *</label>
                    <input
                      type="text"
                      name="nickname"
                      defaultValue={selectedUser?.nickname || ''}
                      required
                      className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Prénom *</label>
                      <input
                        type="text"
                        name="firstname"
                        defaultValue={selectedUser?.firstname || ''}
                        required
                        className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Nom *</label>
                      <input
                        type="text"
                        name="lastname"
                        defaultValue={selectedUser?.lastname || ''}
                        required
                        className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                      />
                    </div>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Email *</label>
                    <input
                      type="email"
                      name="email"
                      defaultValue={selectedUser?.email || ''}
                      required
                      className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                    />
                    {userFormMode === 'edit' && (
                      <p className={`text-xs mt-1 ${isDark ? 'text-orange-400' : 'text-orange-600'}`}>
                        ⚠️ Modifier l'email désactivera le compte jusqu'à vérification
                      </p>
                    )}
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Rôle *</label>
                    <select
                      name="role"
                      defaultValue={selectedUser?.id_role || 1}
                      className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                    >
                      <option value="1">Utilisateur</option>
                      <option value="2">Créateur</option>
                      <option value="3">Admin</option>
                    </select>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Statut du compte *</label>
                    <select
                      name="status"
                      defaultValue={selectedUser?.status || 'active'}
                      className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                    >
                      <option value="active">Actif</option>
                      <option value="inactive">Inactif</option>
                    </select>
                    {userFormMode === 'edit' && selectedUser?.status === 'inactive' && (
                      <p className={`text-xs mt-1 ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                        ⚠️ Cet utilisateur ne pourra pas se connecter
                      </p>
                    )}
                  </div>
                  <div>
                    <input
                      type="password"
                      name="password"
                      required={userFormMode === 'create'}
                      placeholder={userFormMode === 'edit' ? 'Laisser vide pour ne pas modifier' : ''}
                      minLength={8}
                      className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                    />
                    <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Minimum 8 caractères</p>
                  </div>
                  <div className="flex gap-2 justify-end pt-4">
                    <button
                      type="button"
                      onClick={() => setUserModalOpen(false)}
                      className={`px-4 py-2 rounded-lg border ${isDark ? 'border-white/10 hover:bg-white/5' : 'border-gray-200 hover:bg-gray-50'}`}
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-600"
                    >
                      {userFormMode === 'create' ? 'Créer' : 'Modifier'}
                    </button>
                  </div>
                </form>
              )}
            </ModalAdmin>
            
            {/* Modal Commande */}
            <ModalAdmin
              isOpen={orderModalOpen}
              onClose={() => setOrderModalOpen(false)}
              title="Détails de la commande"
              size="lg"
            >
              {selectedOrder && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>N° Commande</label>
                      <p className="font-semibold">{selectedOrder.order_id}</p>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Date</label>
                      <p>{selectedOrder.date}</p>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Client</label>
                      <p>{selectedOrder.client}</p>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Service</label>
                      <p>{selectedOrder.service}</p>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Montant</label>
                      <p className="text-lg font-semibold text-rose-500">{selectedOrder.amount}€</p>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Statut</label>
                      <select
                        defaultValue={selectedOrder.status}
                        className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10' : 'bg-white border-gray-200'}`}
                      >
                        <option>Nouveau</option>
                        <option>En cours</option>
                        <option>Complété</option>
                        <option>Annulé</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex gap-2 justify-end pt-4 border-t">
                    <button
                      onClick={() => setOrderModalOpen(false)}
                      className={`px-4 py-2 rounded-lg border ${isDark ? 'border-white/10 hover:bg-white/5' : 'border-gray-200 hover:bg-gray-50'}`}
                    >
                      Fermer
                    </button>
                    <button className="px-4 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-600">
                      Enregistrer
                    </button>
                  </div>
                </div>
              )}
            </ModalAdmin>
            
            {/* Modal Message */}
            <ModalAdmin
              isOpen={messageModalOpen}
              onClose={() => setMessageModalOpen(false)}
              title="Message de contact"
              size="md"
            >
              {selectedMessage && (
                <div className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Nom</label>
                    <p>{selectedMessage.name}</p>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Email</label>
                    <p>{selectedMessage.email}</p>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Sujet</label>
                    <p className="font-semibold">{selectedMessage.subject}</p>
                  </div>
                  {selectedMessage.request_type && (
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Type de demande</label>
                      <span className={`inline-block px-3 py-1 text-sm rounded-full ${
                        selectedMessage.request_type === 'support' 
                          ? isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-700'
                          : selectedMessage.request_type === 'creation'
                          ? isDark ? 'bg-purple-500/20 text-purple-400' : 'bg-purple-100 text-purple-700'
                          : isDark ? 'bg-gray-500/20 text-gray-400' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {selectedMessage.request_type === 'support' ? 'Support / Assistance' : selectedMessage.request_type === 'creation' ? 'Demande de création' : 'Autre'}
                      </span>
                    </div>
                  )}
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Message</label>
                    <p className={`p-3 rounded-lg ${isDark ? 'bg-[#0b0b0c] border border-white/10' : 'bg-gray-50 border border-gray-200'}`}>
                      {selectedMessage.message}
                    </p>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Réponse</label>
                    <textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      rows="5"
                      className={`w-full px-3 py-2 rounded-lg border resize-none ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                      placeholder="Saisissez votre réponse..."
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Date</label>
                    <p>{selectedMessage.created_at}</p>
                  </div>
                  <div className="flex gap-2 justify-end pt-4 border-t">
                    <button
                      onClick={() => setMessageModalOpen(false)}
                      className={`px-4 py-2 rounded-lg border ${isDark ? 'border-white/10 hover:bg-white/5' : 'border-gray-200 hover:bg-gray-50'}`}
                    >
                      Fermer
                    </button>
                    <button
                      onClick={handleReplyMessage}
                      className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                    >
                      Répondre
                    </button>
                  </div>
                </div>
              )}
            </ModalAdmin>
            
            {/* Modal Produit */}
            <ModalAdmin
              isOpen={productModalOpen}
              onClose={() => {
                setProductModalOpen(false);
                setUploadedImages([]);
                setImagePreviews([]);
                setProductFiles([]);
              }}
              title={productFormMode === 'create' ? 'Créer un produit' : productFormMode === 'edit' ? 'Modifier le produit' : 'Détails du produit'}
              size="lg"
            >
              {productFormMode !== 'view' ? (
                <form 
                  key={`${productFormMode}-${selectedProduct?.id_product || selectedProduct?.id || 'new'}`}
                  onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target);
                  handleSaveProduct({
                    name: formData.get('name'),
                    category: selectedCategory,
                    price: parseFloat(formData.get('price')),
                    stock: parseInt(formData.get('stock')),
                    sale_type: formData.get('sale_type'),
                    description: formData.get('description'),
                    status: formData.get('status'),
                    creator_type: formData.get('creator_type'),
                    id_user_creator: formData.get('id_user_creator') || null,
                    creator_name: formData.get('creator_name') || null,
                  });
                }} className="space-y-4">
                  {productFormMode === 'edit' && selectedProduct && (
                    <div className={`rounded-2xl border p-4 ${isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-gradient-to-br from-white to-gray-50'}`}>
                      <div className="flex flex-wrap items-start justify-between gap-4">
                        <div>
                          <p className={`text-xs font-semibold uppercase tracking-[0.2em] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            Resume du produit
                          </p>
                          <h3 className="mt-2 text-lg font-semibold">
                            {selectedProduct?.name || 'Produit en cours'}
                          </h3>
                          <p className={`mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                            Retrouvez ici la categorie, les visuels et les fichiers deja rattaches pour modifier rapidement la fiche.
                          </p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <span className={`rounded-full px-3 py-1 text-xs font-semibold ${isDark ? 'bg-white/10 text-white/80' : 'bg-white text-gray-700 border border-gray-200'}`}>
                            {selectedCategory || selectedProduct?.category || 'Sans categorie'}
                          </span>
                          <span className={`rounded-full px-3 py-1 text-xs font-semibold ${productSaleType === 'unique' ? (isDark ? 'bg-purple-500/15 text-purple-200 border border-purple-500/20' : 'bg-purple-50 text-purple-700 border border-purple-200') : (isDark ? 'bg-blue-500/15 text-blue-200 border border-blue-500/20' : 'bg-blue-50 text-blue-700 border border-blue-200')}`}>
                            {productSaleType === 'unique' ? 'Vente unique' : 'Vente libre'}
                          </span>
                        </div>
                      </div>

                      <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                        <div className={`rounded-xl border p-3 ${isDark ? 'border-white/10 bg-[#0b0b0c]' : 'border-gray-200 bg-white'}`}>
                          <p className={`text-xs uppercase tracking-[0.18em] ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Categorie</p>
                          <p className="mt-2 font-semibold">{selectedCategory || selectedProduct?.category || 'A definir'}</p>
                        </div>
                        <div className={`rounded-xl border p-3 ${isDark ? 'border-white/10 bg-[#0b0b0c]' : 'border-gray-200 bg-white'}`}>
                          <p className={`text-xs uppercase tracking-[0.18em] ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Visuels</p>
                          <p className="mt-2 font-semibold">
                            {Array.isArray(selectedProduct?.images) && selectedProduct.images.length > 0
                              ? `${selectedProduct.images.length} image(s)`
                              : resolveProductImageUrl(selectedProduct)
                                ? '1 image'
                                : 'Aucune image'}
                          </p>
                        </div>
                        <div className={`rounded-xl border p-3 ${isDark ? 'border-white/10 bg-[#0b0b0c]' : 'border-gray-200 bg-white'}`}>
                          <p className={`text-xs uppercase tracking-[0.18em] ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Fichiers</p>
                          <p className="mt-2 font-semibold">
                            {Array.isArray(selectedProduct?.downloadable_files) && selectedProduct.downloadable_files.length > 0
                              ? `${selectedProduct.downloadable_files.length} fichier(s)`
                              : 'Aucun fichier'}
                          </p>
                        </div>
                        <div className={`rounded-xl border p-3 ${isDark ? 'border-white/10 bg-[#0b0b0c]' : 'border-gray-200 bg-white'}`}>
                          <p className={`text-xs uppercase tracking-[0.18em] ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Statut</p>
                          <p className="mt-2 font-semibold">{selectedProduct?.status === 'active' ? 'Actif' : 'Inactif'}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        Nom du produit <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        defaultValue={productFormValues?.name || ''}
                        required
                        className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        Catégorie <span className="text-rose-500">*</span>
                      </label>
                      <select
                        name="category"
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                        required
                        className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                      >
                        <option value="">-- Sélectionner --</option>
                        {categories.map(cat => (
                          <option key={cat.id_category || cat.id} value={cat.name || cat.name_category}>
                            {cat.name || cat.name_category}
                          </option>
                        ))}
                      </select>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <span className={`rounded-full px-3 py-1 text-xs font-medium ${isDark ? 'bg-white/5 text-white/75 border border-white/10' : 'bg-gray-100 text-gray-700 border border-gray-200'}`}>
                          Selection actuelle : {selectedCategory || 'Aucune categorie'}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        Prix HT (€) <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="number"
                        name="price"
                        step="0.01"
                        defaultValue={productFormValues?.price_ht || productFormValues?.price || ''}
                        required
                        onInput={(e) => {
                          const priceHT = parseFloat(e.target.value) || 0;
                          const tva = priceHT * 0.20;
                          const priceTTC = priceHT + tva;
                          
                          // Mettre à jour la div de calcul
                          const priceDiv = document.querySelector('.price-calc-display-admin');
                          if (priceDiv) {
                            priceDiv.innerHTML = `
                              <div class="text-xs space-y-1.5">
                                <div class="flex justify-between"><span>Prix HT:</span><span class="font-semibold">${priceHT.toFixed(2)}€</span></div>
                                <div class="flex justify-between"><span>TVA (20%):</span><span class="text-orange-500">${tva.toFixed(2)}€</span></div>
                                <div class="border-t border-gray-300 dark:border-white/10 pt-1.5 flex justify-between"><span class="font-semibold">Prix TTC:</span><span class="font-bold text-rose-500">${priceTTC.toFixed(2)}€</span></div>
                              </div>
                            `;
                          }
                        }}
                        className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        Calcul TVA
                      </label>
                      <div className={`price-calc-display-admin w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-gray-400' : 'bg-gray-50 border-gray-200 text-gray-600'}`}>
                        <div className="text-xs text-center opacity-50">Saisissez un prix HT</div>
                      </div>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        Type de vente <span className="text-rose-500">*</span>
                      </label>
                      <select
                        name="sale_type"
                        value={productSaleType}
                        onChange={(e) => {
                          setProductSaleType(e.target.value);
                          if (e.target.value === 'libre') {
                            setAllowedUsers([]);
                          } else if (e.target.value === 'unique' && allowedUsers.length === 0) {
                            // Pré-sélectionner l'utilisateur créateur du produit si c'est vente unique et personne n'est sélectionné
                            if (selectedProduct?.id_user) {
                              setAllowedUsers([String(selectedProduct.id_user)]);
                            } else if (user && user.id_user) {
                              setAllowedUsers([String(user.id_user)]);
                            }
                          }
                        }}
                        className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                      >
                        <option value="libre">Vente libre</option>
                        <option value="unique">Vente unique</option>
                      </select>
                      <input type="hidden" name="creator_type" value={resolvedCreatorType || ''} />
                      <input type="hidden" name="id_user_creator" value={resolvedCreatorId || ''} />
                      <input type="hidden" name="creator_name" value={resolvedCreatorName || ''} />
                    </div>
                  </div>

                  {productSaleType === 'unique' && (
                    <div className={`p-5 rounded-lg border-2 ${isDark ? 'border-purple-500/40 bg-gradient-to-br from-purple-500/10 to-purple-500/5' : 'border-purple-300 bg-gradient-to-br from-purple-50 to-purple-25'}`}>
                      {/* Header avec icône */}
                      <div className="flex items-center gap-2 mb-4">
                        <svg className="w-5 h-5 text-purple-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                        </svg>
                        <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>
                          Utilisateurs autorisés <span className="text-purple-500">*</span>
                        </h3>
                      </div>

                      {/* Description */}
                      <p className={`text-xs mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        <span className="font-semibold text-purple-500">⚠️ Vente unique :</span> Sélectionnez UN SEUL utilisateur qui pourra voir et acheter cette création exclusive.
                      </p>

                      {/* Bouton désélectionner */}
                      {allowedUsers.length > 0 && (
                        <div className="mb-4">
                          <button
                            type="button"
                            onClick={() => setAllowedUsers([])}
                            className={`text-xs px-3 py-1.5 rounded font-medium transition-all ${isDark ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
                          >
                            ✕ Désélectionner
                          </button>
                        </div>
                      )}

                      {/* Résumé des sélectionnés */}
                      {allowedUsers.length > 0 && (
                        <div className={`mb-4 p-3 rounded-lg ${isDark ? 'bg-purple-500/10 border border-purple-500/30' : 'bg-purple-50 border border-purple-200'}`}>
                          <p className={`text-sm font-semibold ${isDark ? 'text-purple-200' : 'text-purple-700'}`}>
                            ✓ {allowedUsers.length} utilisateur{allowedUsers.length > 1 ? 's' : ''} sélectionné{allowedUsers.length > 1 ? 's' : ''}
                          </p>
                          <div className={`mt-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                            {allowedUsers.map((userId, idx) => {
                              const user = users.find(u => String(u.id_user || u.id) === userId);
                              const label = user ? (user.nickname || `${user.firstname ?? ''} ${user.lastname ?? ''}`.trim() || user.email) : '';
                              return (
                                <div key={userId} className="text-sm">
                                  • {label}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Liste des utilisateurs */}
                      <div className="max-h-60 overflow-y-auto border rounded-lg p-3 pr-2" style={{
                        backgroundColor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.6)',
                        borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'
                      }}>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {users.map((u) => {
                            const id = u.id_user || u.id;
                            const label = u.nickname || `${u.firstname ?? ''} ${u.lastname ?? ''}`.trim() || u.email;
                            const checked = allowedUsers.includes(String(id));
                            return (
                              <label key={id} className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                checked 
                                  ? isDark 
                                    ? 'bg-purple-500/15 border-purple-500/50' 
                                    : 'bg-purple-50 border-purple-300'
                                  : isDark
                                    ? 'bg-white/2 border-white/10 hover:bg-white/5'
                                    : 'bg-white/30 border-gray-200 hover:bg-gray-50'
                              }`}>
                                <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                                  checked
                                    ? isDark
                                      ? 'bg-purple-500/40 border-purple-500'
                                      : 'bg-purple-500 border-purple-500'
                                    : isDark
                                      ? 'border-gray-600'
                                      : 'border-gray-300'
                                }`}>
                                  {checked && (
                                    <svg className={`w-3 h-3 ${isDark ? 'text-purple-200' : 'text-white'}`} fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                  )}
                                </div>
                                <input
                                  type="radio"
                                  name="unique_user"
                                  checked={checked}
                                  onChange={() => {
                                    // Pour une vente unique, un seul utilisateur peut être sélectionné
                                    setAllowedUsers([String(id)]);
                                  }}
                                  className="hidden"
                                  aria-label={label}
                                />
                                {u.photo && (
                                  <img
                                    src={u.photo}
                                    alt={label}
                                    className="w-8 h-8 rounded-full object-cover flex-shrink-0 border-2 border-transparent"
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                  />
                                )}
                                <div className="flex-1 min-w-0">
                                  <p className={`text-sm font-medium truncate ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>
                                    {label}
                                  </p>
                                  {u.email && (
                                    <p className={`text-xs truncate ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                                      {u.email}
                                    </p>
                                  )}
                                </div>
                                {checked && (
                                  <div className="flex-shrink-0">
                                    <div className={`text-xs font-semibold px-2 py-1 rounded ${isDark ? 'bg-purple-500/40 text-purple-200' : 'bg-purple-100 text-purple-700'}`}>
                                      ✓
                                    </div>
                                  </div>
                                )}
                              </label>
                            );
                          })}
                          {users.length === 0 && (
                            <div className={`col-span-2 text-center py-4 text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                              Aucun utilisateur trouvé
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Avertissement si vide */}
                      {allowedUsers.length === 0 && (
                        <div className={`mt-3 p-3 rounded-lg text-xs ${isDark ? 'bg-yellow-500/10 text-yellow-300 border border-yellow-500/30' : 'bg-yellow-50 text-yellow-700 border border-yellow-200'}`}>
                          ⚠️ Veuillez sélectionner UN utilisateur pour cette vente unique
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="relative">
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        Stock <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="number"
                        name="stock"
                        min="0"
                        defaultValue={productFormValues?.stock ?? '1'}
                        required
                        placeholder="0 = illimité"
                        className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                      />
                      <button
                        type="button"
                        onClick={(e) => {
                          const input = e.target.parentElement.querySelector('input[name="stock"]');
                          if (input) {
                            input.value = 0;
                            input.dispatchEvent(new Event('input', { bubbles: true }));
                          }
                        }}
                        className={`absolute top-0 right-0 mt-1 mr-1 text-xs px-2 py-1 rounded ${isDark ? 'bg-rose-500/20 text-rose-200 hover:bg-rose-500/30' : 'bg-rose-100 text-rose-700 hover:bg-rose-200'}`}
                      >
                        Illimité
                      </button>
                      <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        0 = Stock illimité
                      </p>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        Statut <span className="text-rose-500">*</span>
                      </label>
                      <select
                        name="status"
                        defaultValue={productFormValues?.status || 'active'}
                        className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                      >
                        <option value="active">Actif</option>
                        <option value="inactive">Inactif</option>
                      </select>
                    </div>
                  </div>

                  {productFormMode === 'edit' && (
                    <div className="grid gap-3 sm:grid-cols-3">
                      <div className={`rounded-xl border p-3 ${isDark ? 'border-white/10 bg-[#0b0b0c]' : 'border-gray-200 bg-gray-50'}`}>
                        <p className={`text-xs uppercase tracking-[0.18em] ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Prix actuel</p>
                        <p className="mt-2 text-lg font-semibold">{productFormValues?.price || productFormValues?.price_ht || 0}€</p>
                      </div>
                      <div className={`rounded-xl border p-3 ${isDark ? 'border-white/10 bg-[#0b0b0c]' : 'border-gray-200 bg-gray-50'}`}>
                        <p className={`text-xs uppercase tracking-[0.18em] ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Stock actuel</p>
                        <p className="mt-2 text-lg font-semibold">{productFormValues?.stock ?? 0}</p>
                      </div>
                      <div className={`rounded-xl border p-3 ${isDark ? 'border-white/10 bg-[#0b0b0c]' : 'border-gray-200 bg-gray-50'}`}>
                        <p className={`text-xs uppercase tracking-[0.18em] ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Acces</p>
                        <p className="mt-2 text-lg font-semibold">{productSaleType === 'unique' ? 'Restreint' : 'Public'}</p>
                      </div>
                    </div>
                  )}
                  
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      Description
                    </label>
                    <textarea
                      name="description"
                      rows="3"
                      defaultValue={productFormValues?.description || ''}
                      className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                    />
                  </div>
                  
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      Image du produit {productFormMode === 'create' && <span className="text-rose-500">*</span>}
                    </label>
                    <div className={`border-2 border-dashed rounded-lg p-4 ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={handleImageUpload}
                        required={productFormMode === 'create' && uploadedImages.length === 0}
                        className={`hidden w-full ${isDark ? 'text-white' : 'text-gray-800'}`}
                      />
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                          isDark 
                            ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                            : 'bg-blue-500 hover:bg-blue-600 text-white'
                        }`}
                      >
                        Ajouter une ou plusieurs images
                      </button>
                      <p className={`mt-3 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        Vous pouvez ajouter les images une par une ou par lot, jusqu'a 8 au total.
                      </p>
                      <p className={`mt-1 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        Les images deja ajoutees restent conservees tant que vous ne les retirez pas manuellement.
                      </p>
                      {(() => {
                        const currentImages = Array.isArray(selectedProduct?.images) && selectedProduct.images.length > 0
                          ? selectedProduct.images.map((img) => resolveProductImageUrl(img)).filter(Boolean)
                          : [resolveProductImageUrl(selectedProduct)].filter(Boolean);

                        return currentImages.length > 0 ? (
                          <div className="mt-4">
                            <p className={`mb-2 text-xs font-semibold uppercase tracking-[0.18em] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                              Images actuelles
                            </p>
                            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">
                              {currentImages.map((url, index) => (
                                <Watermark key={`existing-${index}`}>
                                  <img
                                    src={url}
                                    alt={`${selectedProduct?.name || 'Image'} ${index + 1}`}
                                    className="h-28 w-full rounded-lg object-cover ring-1 ring-black/5"
                                  />
                                </Watermark>
                              ))}
                            </div>
                          </div>
                        ) : null;
                      })()}

                      {imagePreviews.length > 0 ? (
                        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">
                          {imagePreviews.map((preview, index) => (
                            <div key={`preview-${index}`} className="relative group overflow-hidden rounded-2xl">
                              <Watermark>
                                <img
                                  src={preview}
                                  alt={`Aperçu ${index + 1}`}
                                  className="h-28 w-full rounded-lg object-cover"
                                />
                              </Watermark>
                              <button
                                type="button"
                                onClick={() => handleRemoveUploadedImg(index)}
                                className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full border border-white/30 bg-black/55 text-white shadow-lg backdrop-blur-sm transition hover:scale-105 hover:bg-rose-600"
                                title="Retirer"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </div>
                    <p className={`text-xs mt-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      💡 Jusqu'à 8 images compressees. La première devient l'image principale du produit.
                    </p>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      Fichiers telechargeables
                    </label>
                    <input
                      type="file"
                      multiple
                      accept=".zip,.pdf,.jpg,.jpeg,.png,.webp"
                      onChange={handleProductFilesChange}
                      className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                    />
                    {productFiles.length > 0 && (
                      <p className={`text-xs mt-2 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                        {productFiles.length} fichier(s) selectionne(s)
                      </p>
                    )}
                    {Array.isArray(selectedProduct?.downloadable_files) && selectedProduct.downloadable_files.length > 0 && (
                      <div className={`mt-3 rounded-lg border p-3 ${isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-gray-50'}`}>
                        <p className={`mb-2 text-xs font-semibold uppercase tracking-[0.18em] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          Fichiers deja associes
                        </p>
                        <div className="space-y-1.5">
                          {selectedProduct.downloadable_files.map((file, index) => (
                            <p key={`${file.id || file.original_filename || index}`} className={`text-xs ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                              {file.original_filename || `Fichier ${index + 1}`}
                            </p>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex gap-2 justify-end pt-4">
                    <button
                      type="button"
                      onClick={() => setProductModalOpen(false)}
                      className={`px-4 py-2 rounded-lg border ${isDark ? 'border-white/10 hover:bg-white/5' : 'border-gray-200 hover:bg-gray-50'}`}
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-600"
                    >
                      {productFormMode === 'create' ? 'Créer' : 'Modifier'}
                    </button>
                  </div>
                </form>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Nom</label>
                      <p className="font-semibold">{selectedProduct?.name}</p>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Catégorie</label>
                      <p>{selectedProduct?.category}</p>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Type de créateur</label>
                      <span className={`px-2 py-1 rounded text-xs ${
                        selectedProduct?.creator_type === 'krayne' ? 'bg-rose-500/20 text-rose-500' : 'bg-orange-500/20 text-orange-500'
                      }`}>
                        {selectedProduct?.creator_type === 'krayne' ? 'Krayne Agency' : 'Créateur externe'}
                      </span>
                    </div>
                    {selectedProduct?.creator_name && (
                      <div>
                        <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Nom du créateur</label>
                        <p className="font-semibold">{selectedProduct.creator_name}</p>
                      </div>
                    )}
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Prix HT</label>
                      <p className="font-semibold">{selectedProduct?.price}€</p>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Calcul de prix</label>
                      <div className={`p-2 rounded text-xs ${isDark ? 'bg-[#0b0b0c] border border-white/10' : 'bg-gray-50 border border-gray-200'}`}>
                        <div>TVA (20%): {(selectedProduct?.price * 0.20).toFixed(2)}€</div>
                        {selectedProduct?.creator_type === 'external' && (
                          <div className="text-rose-500">Commission Krayne (5%): {(selectedProduct?.price * 0.05).toFixed(2)}€</div>
                        )}
                        <div className="font-semibold text-rose-500 mt-1">
                          Prix TTC: {(selectedProduct?.price * (selectedProduct?.creator_type === 'external' ? 1.25 : 1.20)).toFixed(2)}€
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Stock</label>
                      <p>{selectedProduct?.stock}</p>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Type de vente</label>
                      <span className={`px-2 py-1 rounded text-xs ${
                        selectedProduct?.sale_type === 'unique' ? 'bg-purple-500/20 text-purple-500' : 'bg-blue-500/20 text-blue-500'
                      }`}>
                        {selectedProduct?.sale_type === 'unique' ? 'Unique' : 'Libre'}
                      </span>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Statut</label>
                      <span className={`px-2 py-1 rounded text-xs ${
                        selectedProduct?.status === 'active' ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'
                      }`}>
                        {selectedProduct?.status === 'active' ? 'Actif' : 'Inactif'}
                      </span>
                    </div>
                  </div>
                  {selectedProduct?.description && (
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Description</label>
                      <p className={`p-3 rounded-lg ${isDark ? 'bg-[#0b0b0c] border border-white/10' : 'bg-gray-50 border border-gray-200'}`}>
                        {selectedProduct.description}
                      </p>
                    </div>
                  )}
                  {(() => {
                    const galleryImages = Array.isArray(selectedProduct?.images) && selectedProduct.images.length > 0
                      ? selectedProduct.images.map((img) => resolveProductImageUrl(img)).filter(Boolean)
                      : [resolveProductImageUrl(selectedProduct)].filter(Boolean);

                    if (galleryImages.length === 0) {
                      return null;
                    }

                    const activeImage = galleryImages[Math.min(selectedAdminGalleryImage, galleryImages.length - 1)] || galleryImages[0];

                    return (
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Galerie</label>
                        <div className={`overflow-hidden rounded-2xl border ${isDark ? 'border-white/10 bg-[#0b0b0c]' : 'border-gray-200 bg-gray-50'}`}>
                          <Watermark>
                            <img
                              src={activeImage}
                              alt={selectedProduct?.name}
                              className="h-80 w-full object-cover"
                            />
                          </Watermark>
                        </div>

                        {galleryImages.length > 1 && (
                          <div className="mt-3 grid grid-cols-4 gap-3">
                            {galleryImages.slice(0, 8).map((url, index) => (
                              <button
                                key={`admin-gallery-thumb-${index}`}
                                type="button"
                                onClick={() => setSelectedAdminGalleryImage(index)}
                                className={`overflow-hidden rounded-xl border transition-all ${
                                  selectedAdminGalleryImage === index
                                    ? 'border-rose-500 ring-2 ring-rose-500/30'
                                    : isDark
                                    ? 'border-white/10 hover:border-white/30'
                                    : 'border-gray-200 hover:border-gray-300'
                                }`}
                              >
                                <Watermark>
                                  <img
                                    src={url}
                                    alt={`${selectedProduct?.name} ${index + 1}`}
                                    className="h-24 w-full object-cover"
                                  />
                                </Watermark>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  <div className="flex gap-2 justify-end pt-4 border-t">
                    <button
                      onClick={() => setProductModalOpen(false)}
                      className={`px-4 py-2 rounded-lg border ${isDark ? 'border-white/10 hover:bg-white/5' : 'border-gray-200 hover:bg-gray-50'}`}
                    >
                      Fermer
                    </button>
                    <button 
                      onClick={() => {
                        setProductFormMode('edit');
                      }}
                      className="px-4 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-600"
                    >
                      Modifier
                    </button>
                  </div>
                </div>
              )}
            </ModalAdmin>
            
            {/* Modal Créateur */}
            <ModalAdmin
              isOpen={creatorModalOpen}
              onClose={() => setCreatorModalOpen(false)}
              title={creatorFormMode === 'create' ? 'Créer un créateur' : creatorFormMode === 'edit' ? 'Modifier le créateur' : 'Détails du créateur'}
              size="md"
            >
              {creatorFormMode !== 'view' ? (
                <form onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target);
                  handleSaveCreator({
                    nickname: formData.get('nickname'),
                    firstname: formData.get('firstname'),
                    lastname: formData.get('lastname'),
                    email: formData.get('email'),
                    password: formData.get('password')
                  });
                }} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        Pseudo <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="nickname"
                        defaultValue={selectedCreator?.nickname || ''}
                        required
                        className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        Prénom <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="firstname"
                        defaultValue={selectedCreator?.firstname || ''}
                        required
                        className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        Nom <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="lastname"
                        defaultValue={selectedCreator?.lastname || ''}
                        required
                        className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        Email <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        defaultValue={selectedCreator?.email || ''}
                        required
                        className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      Mot de passe {creatorFormMode === 'create' && <span className="text-rose-500">*</span>}
                    </label>
                    <input
                      type="password"
                      name="password"
                      placeholder={creatorFormMode === 'edit' ? 'Laisser vide pour ne pas changer' : ''}
                      required={creatorFormMode === 'create'}
                      className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                    />
                    <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      {creatorFormMode === 'create' ? 'Minimum 8 caractères' : 'Laisser vide pour conserver le mot de passe actuel'}
                    </p>
                  </div>
                  
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      Photo de profil
                    </label>
                    <div className={`border-2 border-dashed rounded-lg p-4 ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleCreatorImageUpload}
                        className={`w-full ${isDark ? 'text-white' : 'text-gray-800'}`}
                      />
                      {uploadedCreatorImage && (
                        <div className="mt-3">
                          <p className="text-sm text-green-500">✓ Image sélectionnée: {uploadedCreatorImage.name}</p>
                        </div>
                      )}
                      {selectedCreator?.picture_user && !uploadedCreatorImage && (
                        <div className="mt-3">
                          <img 
                            src={selectedCreator.picture_user} 
                            alt="Aperçu" 
                            className="h-20 w-20 object-cover rounded-full mx-auto"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex gap-2 justify-end pt-4">
                    <button
                      type="button"
                      onClick={() => setCreatorModalOpen(false)}
                      className={`px-4 py-2 rounded-lg border ${isDark ? 'border-white/10 hover:bg-white/5' : 'border-gray-200 hover:bg-gray-50'}`}
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-600"
                    >
                      {creatorFormMode === 'create' ? 'Créer' : 'Modifier'}
                    </button>
                  </div>
                </form>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Pseudo</label>
                      <p className="font-semibold">{selectedCreator?.nickname}</p>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Nom complet</label>
                      <p>{selectedCreator?.firstname} {selectedCreator?.lastname}</p>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Email</label>
                      <p>{selectedCreator?.email}</p>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Rôle</label>
                      <span className="px-2 py-1 rounded text-xs bg-purple-500/20 text-purple-500">
                        {selectedCreator?.role}
                      </span>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Date d'inscription</label>
                      <p>{selectedCreator?.created_at ? new Date(selectedCreator.created_at).toLocaleDateString('fr-FR') : '-'}</p>
                    </div>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Statut du compte</label>
                    <div className="flex items-center justify-between">
                      <p className={selectedCreator.status === 'active' ? 'text-green-500' : 'text-red-500'}>
                        {selectedCreator.status === 'active' ? '✓ Actif' : '✕ Inactif'}
                      </p>
                      <button
                        onClick={async () => {
                          const newStatus = selectedCreator.status === 'active' ? 'inactive' : 'active';
                          try {
                            await api.put(`/admin/users/${selectedCreator.id_user || selectedCreator.id}`, {
                              ...selectedCreator,
                              status: newStatus
                            });
                            toast.success(`Créateur ${newStatus === 'active' ? 'activé' : 'désactivé'}`);
                            setSelectedCreator({ ...selectedCreator, status: newStatus });
                            fetchCreators();
                          } catch (error) {
                            toast.error('Erreur lors du changement de statut');
                          }
                        }}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                          selectedCreator.status === 'active'
                            ? 'bg-red-500/10 text-red-500 hover:bg-red-500/20'
                            : 'bg-green-500/10 text-green-500 hover:bg-green-500/20'
                        }`}
                      >
                        {selectedCreator.status === 'active' ? 'Désactiver' : 'Activer'}
                      </button>
                    </div>
                  </div>
                  {selectedCreator?.picture_user && (
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Photo de profil</label>
                      <img 
                        src={selectedCreator.picture_user} 
                        alt={selectedCreator.nickname} 
                        className="h-32 w-32 object-cover rounded-full mx-auto"
                      />
                    </div>
                  )}
                  <div className="flex gap-2 justify-end pt-4 border-t">
                    <button
                      onClick={() => setCreatorModalOpen(false)}
                      className={`px-4 py-2 rounded-lg border ${isDark ? 'border-white/10 hover:bg-white/5' : 'border-gray-200 hover:bg-gray-50'}`}
                    >
                      Fermer
                    </button>
                    <button 
                      onClick={() => {
                        setCreatorFormMode('edit');
                      }}
                      className="px-4 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-600"
                    >
                      Modifier
                    </button>
                  </div>
                </div>
              )}
            </ModalAdmin>

            {/* Modal Projet */}
            <ModalAdmin
              isOpen={projectModalOpen}
              onClose={() => setProjectModalOpen(false)}
              title={projectFormMode === 'create' ? 'Créer un projet' : projectFormMode === 'edit' ? 'Modifier le projet' : 'Détails du projet'}
              size="md"
            >
              {projectFormMode === 'view' && selectedProject ? (
                <div className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Nom du projet</label>
                    <p className={isDark ? 'text-white' : 'text-gray-800'}>{selectedProject.name}</p>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Client</label>
                    <p className={isDark ? 'text-white' : 'text-gray-800'}>{selectedProject.user_name}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Type</label>
                      <p className={isDark ? 'text-white' : 'text-gray-800'}>{selectedProject.type || '-'}</p>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Statut</label>
                      <p className={isDark ? 'text-white' : 'text-gray-800'}>{selectedProject.status}</p>
                    </div>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Progression</label>
                    <p className={isDark ? 'text-white' : 'text-gray-800'}>{selectedProject.progress}%</p>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Description</label>
                    <p className={isDark ? 'text-white' : 'text-gray-800'}>{selectedProject.description || '-'}</p>
                  </div>
                </div>
              ) : (
                <form onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target);
                  const projectData = {
                    id_user: formData.get('id_user'),
                    name: formData.get('name'),
                    type: formData.get('type'),
                    status: formData.get('status'),
                    progress: parseInt(formData.get('progress')) || 0,
                    description: formData.get('description'),
                    start_date: formData.get('start_date') || null,
                    end_date: formData.get('end_date') || null
                  };
                  handleSaveProject(projectData);
                }} className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Client *</label>
                    <select
                      name="id_user"
                      defaultValue={selectedProject?.id_user || ''}
                      required
                      className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                    >
                      <option value="">Sélectionnez un client</option>
                      {users.map(u => (
                        <option key={u.id_user} value={u.id_user}>{u.name} ({u.email})</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Nom du projet *</label>
                    <input
                      type="text"
                      name="name"
                      defaultValue={selectedProject?.name || ''}
                      required
                      className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Type</label>
                      <input
                        type="text"
                        name="type"
                        defaultValue={selectedProject?.type || ''}
                        className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Statut</label>
                      <select
                        name="status"
                        defaultValue={selectedProject?.status || 'En cours'}
                        className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                      >
                        <option value="En cours">En cours</option>
                        <option value="En attente">En attente</option>
                        <option value="Terminé">Terminé</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Progression (%)</label>
                    <input
                      type="number"
                      name="progress"
                      min="0"
                      max="100"
                      defaultValue={selectedProject?.progress || 0}
                      className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Description</label>
                    <textarea
                      name="description"
                      rows="4"
                      defaultValue={selectedProject?.description || ''}
                      className={`w-full px-3 py-2 rounded-lg border resize-none ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                    />
                  </div>
                  <div className="flex gap-2 justify-end pt-4 border-t">
                    <button
                      type="button"
                      onClick={() => setProjectModalOpen(false)}
                      className={`px-4 py-2 rounded-lg border ${isDark ? 'border-white/10 hover:bg-white/5' : 'border-gray-200 hover:bg-gray-50'}`}
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-600"
                    >
                      {projectFormMode === 'create' ? 'Créer' : 'Enregistrer'}
                    </button>
                  </div>
                </form>
              )}
            </ModalAdmin>

            {/* Modal Catégories */}
            <ModalAdmin
              isOpen={categoryModalOpen}
              onClose={() => {
                setCategoryModalOpen(false);
                setSelectedCategoryData(null);
              }}
              title={categoryFormMode === 'create' ? 'Ajouter une catégorie' : 'Modifier la catégorie'}
            >
              <form onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                handleSaveCategory({
                  name: formData.get('name'),
                  slug: formData.get('slug')
                });
              }} className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Nom de la catégorie <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    defaultValue={selectedCategoryData?.name || selectedCategoryData?.name_category || ''}
                    required
                    className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                    placeholder="Ex: Logos, Bannières, Illustrations..."
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Slug <span className="text-gray-400 text-xs">(optionnel, généré automatiquement si vide)</span>
                  </label>
                  <input
                    type="text"
                    name="slug"
                    defaultValue={selectedCategoryData?.slug || ''}
                    className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-800'}`}
                    placeholder="Ex: logos, bannieres..."
                  />
                  <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    Le slug est utilisé dans les URLs. Laissez vide pour génération automatique.
                  </p>
                </div>

                <div className="flex gap-2 justify-end pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => setCategoryModalOpen(false)}
                    className={`px-4 py-2 rounded-lg border ${isDark ? 'border-white/10 hover:bg-white/5' : 'border-gray-200 hover:bg-gray-50'}`}
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-600"
                  >
                    {categoryFormMode === 'create' ? 'Créer' : 'Enregistrer'}
                  </button>
                </div>
              </form>
            </ModalAdmin>
        </div>
    );
}

export default Dashboard;
